// Ho Chi Minh City, "Saigon River after the rain": a warm, humid night as an evening shower clears, on the Thu Thiem
// bank at the south end of the Saigon riverside park, where the old Thu Thiem ferry landed (10.770 N, 106.710 E),
// looking west to north-north-east (about 285° to 25°) across the Saigon River to District 1. From there the view runs,
// left to right: the south end of the District 1 riverfront (250°), the Bitexco Financial Tower 640 m away (286°, its
// helipad cantilevered out of its south face, so to the left), the gold-lit Saigon Times Square tower over the cream
// Majestic Hotel (304°-315°), the Vietcombank Tower with its stepped glass top and mast (326°), Le Meridien, the Marina
// tower and the Ba Son towers along Ton Duc Thang (334°-340°), the Ba Son Bridge crossing from Ba Son (343°) to its white
// hairpin pylon on our own bank (5°, 1 km), and Landmark 81 beyond it, 3 km off in Binh Thanh (25°). The 100° are
// compressed into the frame without changing the order (the right-hand part more); Landmark 81 is drawn at 1.7 times
// its true size from here and the pylon at 1.4, as a long lens brings them in.
// Back to front: a deep violet sky warm with the city's glow, a moon, a few stars, the last of the rain clouds -> the far
// skyline, its windows and LED facades -> Bitexco, its LED crown and lit floors -> the riverfront, Bach Dang's lamps and
// moored dinner boats, motorbikes on Ton Duc Thang -> the bridge and Landmark 81 -> the river, mirrored and streaked,
// with hyacinths, the waterbus and a dinner cruise -> our bank's railing and lamps, with walkers, a sweeper, a couple, a
// filmer and a seller of LED toys -> the street-food corner, near and big on the left: a bánh mì cart and its bulb, the
// vendor's brazier, two low tables with their stools and drinkers, parked scooters -> the wet paving and its puddles
// mirroring the city -> the tamarind over the corner, with the corner's lit sign (the billboard) hung from its limb.
//
// Timeline (loop seconds)
//   all loop    walkers on two lanes, the sweeper along the promenade and back, the vendor at her cart and (6 s in every
//               20) fanning her brazier, the tea drinkers eating, sipping and talking, an LED toy shot up every 12 s
//   10, 90, 170 a plane comes down towards Tan Son Nhat (24 s)
//   24-204      a dinner cruise heads downstream, right to left (its horn at 28)
//   40-64       a bat hunts the moths round a lamp (hidden thing; again 150-174)
//   56-84       Bitexco's crown runs its colour show
//   96-166      a rider pulls up at the corner, eats a bánh mì at the second table (110-150) and rides off
//   108-136     the Ba Son pylon's floodlights run through four colours
//   112-192     the waterbus heads upstream, left to right
//   156-186     Landmark 81's light show
//   188-216     a warm shower passes (out comes an umbrella, rings on the puddles); the tamarind drips until 236
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, inEvent, cycle, hash, rng, bayer, hex,
  makeCanvas, ditherFill, makeHalo, drawHalo, glyph, FONT3, reflect, streak, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                          // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };

/* ================= Layout ================= */
const HZ = 158;                     // the far waterline: the foot of District 1's riverfront, nearly the horizon
const RAIL = 179;                   // top rail of our bank's white railing (0.8 m)
const EDGE = 194;                   // our bank's granite edge, the railing's foot: the river shows from HZ down to it
const PROM = EDGE + 2;              // the promenade's first row
// The eye is a little above a standing head (the viewer is on the park's steps): someone standing at row y is
// (y - HZ) / 1.2 tall, 34 px at the railing (199), 38 at the food corner (204). Nobody stands below row 206.
const tall = y => Math.round((y - HZ) / 1.2);
const MOON = { x: 368, y: 36, r: 6 };
// Bitexco Financial Tower (262.5 m, 2010). Measured on a night photo from this bank: the tower is 4.6 times as tall as it
// is wide; the helipad sits at 0.76 of its height (191 m of 262.5 m to its deck, 0.73), and the bud above it, 0.24 of
// the height, is 0.55 of the tower's greatest width at its foot. The bud's tip is over the north (right) edge.
const BX = { tip: 24, ground: HZ, heli: 56, xr: 168, xw: 138, tipX: 162 };
// Landmark 81 (461 m, roof 395 m): a bundle of square tubes stepping up from the sides to the core, whose top carries the
// crown's lit lattice and the white spire (the top 0.14); 4.6 times as tall as its widest.
const LM = { cx: 433, tip: 70, roof: 83, crown: 95, foot: 156 };
// the Ba Son Bridge: its pylon, an inverted U of two legs leaning east, stands on our bank; the deck crosses to Ba Son
const BS = { foot: 162, apex: 110, x0: 387, ax: 399, deckL: 334, deckY: 151 };
const BB = { x: 62, y: 104, w: 48, h: 20 };       // the food corner's lit sign, hung from the tamarind's limb: the billboard
const BLOCK = { x0: 179, x1: 241, roof: 136 };     // a riverfront office block
const TREE = { x: 9, base: 207, limb: 98 };        // the tamarind over the corner: its trunk, and the limb the sign hangs from
const limbY = x => Math.round(TREE.limb - 3 * Math.sin(Math.PI * clamp((x - TREE.x) / 112, 0, 1)) + 1.2 * Math.sin(x * 0.21));
const LAMPS = [236, 350, 462];                     // our promenade's double-globe lamps (x), on the railing's line
const LAMP_Y = 132;                                // their globes' row (3.5 m up)

/* ================= Pixel helpers ================= */
// ImageData painter for pixel-heavy layers
function painter(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data, cache = {};
  const put = (i, y, col, a = 255) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h || !col) return;
    const rgb = cache[col] || (cache[col] = hex(col)), o = (y * w + i) * 4;
    if (a < 255 && d[o + 3]) {                     // blend over what is there
      const f = a / 255;
      d[o] = Math.round(lerp(d[o], rgb[0], f)); d[o + 1] = Math.round(lerp(d[o + 1], rgb[1], f)); d[o + 2] = Math.round(lerp(d[o + 2], rgb[2], f));
      return;
    }
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = a;
  };
  const get = (i, y) => (i < 0 || y < 0 || i >= w || y >= h) ? 0 : d[(y * w + i) * 4 + 3];
  const fill = (x0, y0, ww, hh, col) => { for (let y = y0; y < y0 + hh; y++) for (let i = x0; i < x0 + ww; i++) put(i, y, col); };
  return { c, x, put, get, fill, done: () => { x.putImageData(img, 0, 0); return c; } };
}
// sprite from rows of characters and a colour key ('.' or unknown = transparent)
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
function mixHex(a, b, t) {
  const A = hex(a), B = hex(b), f = k => Math.round(lerp(A[k], B[k], clamp(t, 0, 1))).toString(16).padStart(2, '0');
  return `#${f(0)}${f(1)}${f(2)}`;
}
// pick from a dark-to-light ramp with ordered dither
function ramp(pal, v, i, y) {
  const f = clamp(v, 0, 1) * (pal.length - 1);
  let k = Math.floor(f); if (f - k > bayer(i, y)) k++;
  return pal[Math.min(k, pal.length - 1)];
}

/* ================= Sign font: FONT3 with wide M, N, W, a barred Đ and Vietnamese marks ================= */
// The marks sit two rows up, with a clear row between them and the capital (touching, Á reads as a tall A): grave and
// acute slant, the circumflex is a little roof; a sign leaves three rows over its letters for them. Only the words the signs spell are needed: BÁNH MÌ, TRÀ ĐÁ, CÀ PHÊ, SÀI GÒN.
const WIDE = {
  M: ['10001', '11011', '10101', '10001', '10001'], W: ['10001', '10001', '10101', '10101', '01010'],
  N: ['1001', '1101', '1011', '1001', '1001'], 'Đ': ['0110', '0101', '1101', '0101', '0110'],
};
const MARKS = { '\u0300': ['100', '010'], '\u0301': ['001', '010'], '\u0302': ['010', '101'] };   // grave, acute, circumflex
// a letter and the mark over it: 'À' -> ['A', grave]
function split(ch) {
  const d = ch.normalize('NFD');
  return [d[0], d.length > 1 ? MARKS[d[1]] : null];
}
const glyphOf = ch => WIDE[ch] ?? FONT3[ch];
const signWidth = s => [...s.toUpperCase()].reduce((w, ch) => { const [b] = split(ch); return w + ((WIDE[ch] ?? glyphOf(b))?.[0].length ?? 3) + 1; }, 0) - (s ? 1 : 0);
function sign(c, s, x, y, col) {
  let cx = Math.round(x);
  for (const ch of s.toUpperCase()) {
    const [b, mark] = WIDE[ch] ? [ch, null] : split(ch), g = glyphOf(b);
    if (g) glyph(c, g, cx, Math.round(y), col);
    if (mark) glyph(c, mark, cx + ((g?.[0].length ?? 3) - 3 >> 1), Math.round(y) - 3, col);
    cx += (g?.[0].length ?? 3) + 1;
  }
  return cx - x - 1;
}

/* ================= Sky: deep violet, warm with the city's glow low down ================= */
const SKY = ['#04061a', '#060a22', '#090e2b', '#0d1234', '#12163c', '#181a44', '#201d4a', '#2a214f', '#352552',
  '#422953', '#512e53', '#603451', '#6e3b4f', '#7c444d', '#88504c'];
function renderSky() {
  return ditherFill(W, H, SKY, (i, y) => {
    let v = Math.pow(Math.min(1, y / HZ), 1.7) * 0.8;
    v += Math.max(0, 1 - Math.hypot((i - 250) / 300, (y - HZ) / 70)) * 0.22;      // the city's sodium-and-LED glow
    v += Math.max(0, 1 - Math.hypot((i - MOON.x) / 70, (y - MOON.y) / 50)) * 0.1; // moonlit sky round the moon
    return v;
  });
}
function renderMoon() {
  const r = MOON.r, s = r * 2 + 1, p = painter(s, s);
  const maria = [[-2, -1, 1.6], [1, 2, 1.4], [2, -2, 1], [-1, 3, 0.8]];
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.35) continue;
    let col = dx + dy < -4 ? '#fff6e4' : '#f4e8d0';
    if (dd > r - 1.1) col = dx + dy > 0 ? '#c8b89c' : '#e8dac0';
    for (const [cx, cy, cr] of maria) if (Math.hypot(dx - cx, dy - cy) < cr) col = dd > r - 1.1 ? '#bcae94' : '#dcccb0';
    p.put(i, y, col);
  }
  return p.done();
}
// a humid tropical night: few stars, all high up
const STARS = [];
{
  const r = rng(84);
  while (STARS.length < 34) {
    const x = Math.floor(r() * W), y = 2 + Math.floor(r() * 70);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 16 || (x < 110 && y < 80)) continue;
    STARS.push({ x, y, k: 10 + Math.floor(r() * 40), ph: r(), b: y < 40 ? 0.4 + r() * 0.45 : 0.18 + r() * 0.25, big: r() < 0.07 });
  }
}
// thin rain-cloud remnants drifting off east, dithered into the sky with no outline; tileable so they can drift
const WISPS = [{ seed: 43, y: 18, h: 10, n: 2, k: 1 }, { seed: 5, y: 44, h: 12, n: 3, k: 1 }, { seed: 17, y: 70, h: 12, n: 2, k: 2 }, { seed: 31, y: 96, h: 12, n: 3, k: 1 }, { seed: 59, y: 118, h: 12, n: 2, k: 2 }];
function renderWisp(def) {
  const r = rng(def.seed), h = def.h, blobs = [];
  for (let b = 0; b < def.n; b++) {
    const cx = r() * W, len = 60 + r() * 70;
    for (let k = 0; k < 5; k++) blobs.push({ x: cx + (r() - 0.5) * len, y: h * (0.3 + r() * 0.4), rx: 9 + r() * 15, ry: 1.2 + r() * 1.3 });
  }
  const p = painter(W, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let v = 0;
    for (const b of blobs) {
      let dx = x - b.x; dx -= Math.round(dx / W) * W;
      v = Math.max(v, 1 - (dx * dx) / (b.rx * b.rx) - ((y - b.y) ** 2) / (b.ry * b.ry));
    }
    if (v > 0.62) p.put(x, y, v > 0.84 ? '#3a2c56' : '#2e2650');
    else if (v > 0.24 && (v - 0.24) * 2.4 > bayer(x, y)) p.put(x, y, '#26214a');
  }
  return { c: p.done(), y: def.y, k: def.k };
}

/* ================= The far skyline: District 1's towers, hazed with distance ================= */
// [x0, x1, top, kind, night mix]; kinds: o office glass (cool floors), r residential (warm dots), l LED-outlined,
// g gold LED lines. Built from the photos' order where a tower is known, generic where it is one of many.
const NIGHT = '#141834', HAZE = '#3a2a4e';
const BACK = [];                                   // the far haze layer: low-contrast towers, filled in by layoutBack()
const MID = [
  // the south end of the riverfront and the Ben Nghe canal's mouth (mostly behind the tree and the cart)
  [0, 16, 104, 'o', 0.3], [17, 31, 116, 'r', 0.3], [33, 52, 96, 'o', 0.28], [54, 68, 110, 'r', 0.3], [70, 88, 102, 'l', 0.26],
  [90, 104, 118, 'r', 0.3], [106, 124, 108, 'o', 0.28], [118, 134, 122, 'r', 0.32],
  // round Bitexco and behind the riverfront block
  [168, 184, 100, 'p', 0.22], [184, 198, 116, 'o', 0.3], [198, 214, 106, 'r', 0.3], [214, 230, 120, 'o', 0.32], [230, 244, 112, 'r', 0.3],
  [256, 268, 112, 'o', 0.26], [266, 283, 117, 'w', 0.18],                      // ... and the Renaissance Riverside
  [299, 312, 114, 'r', 0.26], [311, 327, 117, 'o', 0.2],                        // Le Meridien
  [327, 345, 106, 'm', 0.22],                                                   // the Marina tower
  [345, 357, 117, 'g', 0.24], [356, 367, 111, 'g', 0.22], [366, 378, 121, 'g', 0.26],   // the Ba Son towers, gold LED lines
];
// the towers of Vinhomes Central Park round Landmark 81's foot, 3 km off
const VIN = [[398, 410, 134], [410, 421, 125], [445, 455, 127], [455, 466, 133], [466, 480, 128]];   // clear of the tower itself (424-442)
const WINS = [];                                   // windows that switch on and off over the loop
function layoutBack() {
  const r = rng(61);
  let x = -4;
  while (x < W) { const w = 7 + Math.floor(r() * 12); BACK.push([x, x + w, 116 + Math.floor(r() * 24), r() < 0.5 ? 'o' : 'r', 0.6]); x += w - Math.floor(r() * 3); }
}
function drawTower(p, [x0, x1, top, kind, dk], base = HZ) {
  const put = p.put, r = rng(x0 * 31 + top);
  const body = mixHex(kind === 'w' ? '#3a2a34' : kind === 'p' ? '#3a2a40' : '#1c2240', HAZE, dk);
  const lit = mixHex('#2c3456', HAZE, dk), dark = mixHex('#10142a', HAZE, dk * 0.8), edge = mixHex('#46507a', HAZE, dk);
  const warm = mixHex('#ffcf86', HAZE, dk * 0.9), cool = mixHex('#dfe8f4', HAZE, dk * 0.9), gold = mixHex('#ffc45a', HAZE, dk * 0.6);
  for (let y = top; y < base; y++) for (let i = x0; i < x1; i++) {
    let col = (i - x0) < 2 ? lit : body;
    if (i === x1 - 1) col = dark;
    if (y === top) col = edge;
    put(i, y, col);
  }
  if (kind === 'm' || kind === 'o' && x1 - x0 > 14) for (let i = x0 + 1; i < x1 - 1; i++) put(i, top + 1, mixHex('#aab8d8', HAZE, dk));   // a lit crown band
  for (let y = top + 3; y < base - 1; y += 2) for (let i = x0 + 1; i < x1 - 1; i++) {
    const hsh = hash(x0, i, y);
    if (kind === 'g') { if ((i - x0) % 3 === 1) put(i, y, gold), put(i, y + 1, mixHex(gold, body, 0.4)); continue; }
    if (kind === 'l') { if (i === x0 + 1 || i === x1 - 2) put(i, y, cool), put(i, y + 1, cool); else if (hsh < 0.3) put(i, y, warm); continue; }
    if (kind === 'o' || kind === 'm') { if (hsh < 0.42) put(i, y, hsh < 0.3 ? cool : warm); continue; }
    if ((i - x0) % 2 === 1 && hsh < 0.38) put(i, y, warm);
    else if (kind === 'w' && hsh > 0.9) put(i, y, warm);
  }
  if (kind === 'p') for (let y = top + 4; y < base; y += 6) for (let i = x0 + 1; i < x1 - 1; i++) put(i, y, mixHex('#e87ab8', HAZE, dk));   // pink LED bands
  for (let k = dk < 0.5 ? 3 : 1; k > 0; k--) if (r() < 0.7) WINS.push({ x: x0 + 2 + Math.floor(r() * (x1 - x0 - 4)), y: top + 3 + 2 * Math.floor(r() * Math.max(1, (base - top - 6) / 2)), n: 2 + Math.floor(r() * 5), ph: r(), col: warm, off: body });
}
// Saigon Times Square: a slab ruled in gold LED lines from foot to roof, a slim crown
const TSQ = { x0: 238, x1: 259, top: 82 };   // 0.6 of Bitexco's height and 0.7 of its width, as in the photos
function renderTimesSquare(p) {
  const { x0, x1, top } = TSQ, put = p.put;
  for (let y = top; y < HZ; y++) for (let i = x0; i < x1; i++) {
    const k = i - x0;
    let col = k % 3 === 1 ? (y % 2 ? '#ffcf68' : '#f2b04a') : k === 0 ? '#3a2c3a' : '#1e1a2e';
    if (y < top + 2) col = '#ffe2a0';
    put(i, y, col);
  }
  for (let y = top - 4; y < top; y++) for (let i = x0 + 4; i < x1 - 4; i++) put(i, y, y === top - 4 ? '#ffd88a' : '#2a2234');
  put((x0 + x1) >> 1, top - 6, '#ff5a4a'); put((x0 + x1) >> 1, top - 5, '#3a2a30');
}
// the Vietcombank Tower (206 m, Pelli Clarke Pelli): a shaft of vertical fins whose top is a run of glass slabs stepping
// up from the south (left) to the north, lit warm white, with a slim mast rising off-centre from the highest one
const VCB = { x0: 284, x1: 300, roof: 98, spire: 73 };
const VCB_MAST = VCB.x1 - 5;
function renderVietcombank(p) {
  const { x0, x1, roof, spire } = VCB, put = p.put;
  for (let y = roof; y < HZ; y++) for (let i = x0; i < x1; i++) {
    const k = i - x0;
    let col = k % 3 === 0 ? '#4a5880' : '#1e2444';
    if (k === 0) col = '#6a78a0'; else if (i === x1 - 1) col = '#141830';
    if ((y - roof) % 2 === 1 && k % 3 && hash(i, y, 7) < 0.5) col = hash(i, y, 8) < 0.55 ? '#e8eef8' : '#ffd890';
    put(i, y, col);
  }
  for (let i = x0 + 1; i < x1 - 1; i++) { put(i, roof + 1, i % 2 ? '#ffe2a0' : '#c8a070'); put(i, roof + 3, i % 3 ? '#ffd890' : '#6a5040'); }
  // [left inset, top row, right inset]: each slab's top edge bright, its face lit in warm mullions, its right side in shade
  const slabs = [[2, 95, 0], [5, 92, 0], [7, 89, 0], [9, 86, 1], [10, 84, 2]];
  let below = roof;
  for (const [li, ty, ri] of slabs) {
    for (let y = ty; y < below; y++) for (let i = x0 + li; i < x1 - ri; i++) {
      let col = (i - x0) % 2 ? '#d8b888' : '#f2dcae';
      if (y === ty) col = '#fff4dc';
      if (i === x0 + li) col = '#fff0d0';
      if (i >= x1 - ri - 2) col = i === x1 - ri - 1 ? '#3a3042' : '#8a7060';
      put(i, y, col);
    }
    below = ty;
  }
  for (let y = spire; y < 84; y++) { put(VCB_MAST, y, y < spire + 2 ? '#fff4e0' : '#d8c4a4'); if (y > 78) put(VCB_MAST + 1, y, '#6a5a58'); }
}

/* ================= Bitexco Financial Tower: the lotus bud, its helipad and LED crown ================= */
// rows: the bud's tip 24, the LED crown 24-56, the helipad 55-58, the floors below to the riverfront's roofs (~140),
// the foot behind them at HZ. Seen from the east: the north (right) edge rises nearly straight to the tip; the south
// (left) edge swells out to the widest at 0.6 down, the lower petal carries the helipad, and above it the bud curves in.
const BIT = [];                                    // per row: [left, right, petal left] edges (for the per-frame lights)
function bitEdges() {
  const { tip, ground, heli, xr, xw, tipX } = BX, h = ground - tip;
  for (let y = tip; y <= ground; y++) {
    const u = (y - tip) / h, uh = (heli - tip) / h;
    const right = Math.round(xr - 6 * Math.pow(1 - Math.min(1, u / 0.2), 2));
    let left;
    if (y <= heli) left = Math.round(tipX - 11 * Math.pow((y - tip) / (heli - tip), 0.75));   // the bud's curved south face
    else left = Math.round(xw + 9 * Math.pow(1 - Math.min(1, (u - uh) / (0.6 - uh)), 1.6) + (u > 0.6 ? (u - 0.6) * 2.5 : 0));
    BIT[y] = [Math.min(left, right), right, y > heli ? Math.round(lerp(151, 147, Math.min(1, (y - heli) / 30))) : left];
  }
}
const BITC = { glass: '#141c38', glass2: '#1c2648', spine: '#8ea4d0', spine2: '#5a6c9c', floor: '#e8e6dc', warm: '#ffd88e', dim: '#3a4670' };
function renderBitexco() {
  const p = painter(W, H), put = p.put, { tip, ground, heli } = BX;
  for (let y = tip; y <= ground; y++) {
    const [l, rr, pl] = BIT[y];
    for (let i = l; i <= rr; i++) {
      const s = (i - l) / Math.max(1, rr - l);
      let col = s > 0.86 ? BITC.glass2 : BITC.glass;
      if (i === rr) col = BITC.spine; else if (i === rr - 1) col = BITC.spine2;
      if (i === l) col = BITC.dim; else if (s < 0.14) col = '#111934';      // the curve turning away on the south side
      put(i, y, col);
    }
  }
  // the petal's seam: from the helipad's inner end, sweeping down to the north face
  for (let k = 0; k < 40; k++) { const y = heli + 4 + k, x = Math.round(150 + k * 0.4); if (BIT[y] && x < BIT[y][1] - 1) put(x, y, '#1e2a52'); }
  // the mechanical floor's slot band, a third down
  for (let i = BIT[92][0] + 1; i < BIT[92][1]; i++) { put(i, 92, '#46588a'); put(i, 93, '#0c1228'); }
  // the helipad: a flat disc with a lit rim, under it its cantilever
  for (let i = 139; i <= 154; i++) {               // the deck's lit rim, then its underside: a disc seen from below
    put(i, heli - 1, i < 141 || i > 152 ? null : '#aab6d4');
    put(i, heli, i === 139 || i === 154 ? '#8a96b8' : '#e4eaf6');
    put(i, heli + 1, i < 140 || i > 153 ? null : '#4a5478');
    put(i, heli + 2, i < 142 || i > 151 ? null : '#262c4c');
  }
  for (let i = 144; i <= 150; i++) put(i, heli + 3, '#1a2040');
  for (let k = 0; k < 5; k++) { put(147 + k, heli + 4 + k, '#1a2040'); put(148 + k, heli + 4 + k, '#141a36'); }
  return p.done();
}
// the per-frame lights: lit floors (two-pixel storeys), the crown's LED dots, the aviation lamps; a colour show is an event
const BIT_FLOORS = [];
function layoutBitFloors() {
  const r = rng(262);
  for (let y = BX.heli + 4; y < 140; y += 2) {
    if (y === 92 || y === 93) continue;
    const [l, rr] = BIT[y];
    const runs = [];
    let i = l + 1;
    while (i < rr - 1) { const len = 2 + Math.floor(r() * 6); if (r() < 0.62) runs.push([i, Math.min(len, rr - 1 - i), r() < 0.7 ? 0 : 1]); i += len + Math.floor(r() * 3); }
    BIT_FLOORS.push({ y, runs, band: r() < 0.16 });
  }
}
const BIT_SHOW = [56, 84];                         // the crown's colour show (loop seconds)
const SHOW_COLS = ['#5aff8a', '#ff6ac8', '#5ae0ff', '#a87aff', '#ffd25a'];
function drawBitexcoLights(t) {
  const { tip, heli } = BX, sw = step(t, 40);
  for (const F of BIT_FLOORS) for (const [x, w, warm] of F.runs) {
    if (hash(x, F.y, sw) > 0.94) continue;          // now and then an office switches off for a while
    R(x, F.y, w, 1, F.band ? '#f4f6fa' : warm ? BITC.warm : '#dfe4ee');
  }
  // the bud's LED dot grid, white, a soft band of light rising through it every 20 s; in its show, bands of colour climb it
  const band = tip + (heli - tip) * (1 - frac(t / 20)), show = env(t, BIT_SHOW[0], BIT_SHOW[1], 3), sh = step(t, 480);
  for (let y = tip + 4; y < heli - 1; y += 2) {
    const [l, rr] = BIT[y], lift = Math.max(0, 1 - Math.abs(y - band) / 6);
    ctx.fillStyle = lift > 0.3 ? '#ffffff' : '#b8c4dc';
    if (show > 0) ctx.fillStyle = mixHex(lift > 0.3 ? '#ffffff' : '#b8c4dc', SHOW_COLS[(((heli - y) >> 2) + 40 - sh % 40) % SHOW_COLS.length], show);
    for (let i = l + 1 + (y >> 1) % 2; i < rr - 1; i += 2) ctx.fillRect(i, y, 1, 1);
  }
  halo(HL.crown, 158, 40, 0.55 + 0.25 * Math.max(0, 1 - Math.abs(40 - band) / 14));
  // the helipad's rim lights and the red aviation lamps on the tip and the helipad
  for (const x of [141, 146, 152]) R(x, heli, 1, 1, '#ffffff');
  if (frac(t / 1.5) < 0.5) { R(BX.tipX, tip - 1, 1, 1, '#ff4a3a'); halo(HL.red, BX.tipX, tip - 1, 0.9); R(140, heli - 1, 1, 1, '#ff4a3a'); halo(HL.red, 140, heli - 1, 0.6); }
}

/* ================= Landmark 81 and the towers of Vinhomes round its foot ================= */
// From the night photos: nine square tubes in a bundle, the outer ones stopping lower, so each side steps up to the core
// in flat lit tops; every tube's face is ruled in warm-white vertical LED lines with a dark seam between tubes, the tubes
// facing the city (left) brighter. The core carries the crown, a dotted lattice that steps in once, and the white spire.
const LM_TUBES = [[-9, 4, 0.44, 1], [-5, 3, 0.64, 1], [-2, 5, 0, 1], [3, 3, 0.54, 0], [6, 4, 0.28, 0]];   // [x from cx, width, top (share of height; 0 = the crown), lit]
function renderLandmark81(p) {
  const put = p.put, { cx, tip, roof, crown, foot } = LM, h = foot - tip;
  for (const [dx, w, f, lit] of LM_TUBES) {
    const x0 = cx + dx, ty = f ? foot - Math.round(f * h) : crown;
    for (let y = ty; y < foot; y++) for (let i = x0; i < x0 + w; i++) {
      const k = i - x0, low = Math.max(0, (y - (foot - 18)) / 18);
      let col = k === w - 1 ? (lit ? '#6a5a5c' : '#4a3e48') : k === 0 ? (lit ? '#fff6e2' : '#b8a68c') : k % 2 ? (lit ? '#9a8670' : '#5e5058') : (lit ? '#f0dcb8' : '#c4ae8c');
      if (k < w - 1 && k % 2 === 0) col = mixHex(col, '#fff4dc', low * 0.5);
      if (y === ty) col = k === w - 1 ? '#8a7a70' : lit ? '#ffffff' : '#e8dcc8';
      if (y === ty + 1 && k < w - 1) col = lit ? '#5a4c50' : '#3a3240';   // the shadow under each flat top
      put(i, y, col);
    }
  }
  for (let y = roof; y < crown; y++) {             // the crown: a dotted lattice, stepping in four rows down
    const ins = y < roof + 5 ? 1 : 0;
    for (let i = cx - 2 + ins; i <= cx + 2 - ins; i++) put(i, y, (i + y) % 2 ? '#3a3248' : '#fff2dc');
  }
  for (let y = tip; y < roof; y++) {               // the ribbed spire
    put(cx, y, '#ffffff');
    if (y > tip + 3 && y % 2) { put(cx - 1, y, '#c8c0d4'); put(cx + 1, y, '#9a92aa'); }
  }
  for (const [x0, x1, ty] of VIN) drawTower(p, [x0, x1, ty, 'r', 0.3], 158);
}

/* ================= The Ba Son Bridge (2022): its hairpin pylon on our bank, the deck crossing to Ba Son ================= */
// The pylon is a tall inverted U whose legs straddle the deck; seen from the south at an angle the legs stand a few
// pixels apart, leaning east, joined in a rounded top. Its stays fan down on both sides.
function line(p, x0, y0, x1, y1, col, a = 255) {
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
  for (let k = 0; k <= n; k++) p.put(lerp(x0, x1, k / n), lerp(y0, y1, k / n), col, a);
}
const PYLON_FOOT = 389;
const deckY = x => x <= PYLON_FOOT + 2
  ? Math.round(lerp(BS.deckY + 2, 157, (x - BS.deckL) / (PYLON_FOOT + 2 - BS.deckL)) - 2 * Math.sin(Math.PI * clamp((x - BS.deckL) / (PYLON_FOOT + 2 - BS.deckL), 0, 1)))
  : Math.round(lerp(157, 160, (x - PYLON_FOOT - 2) / (W - PYLON_FOOT - 2)));
const legX = (y, back) => lerp(back ? 401 : 398, back ? PYLON_FOOT + 5 : PYLON_FOOT, (y - 111) / (BS.foot - 111));
function renderBridge() {
  const p = painter(W, H), put = p.put;
  for (let k = 0; k < 7; k++) { const ay = 115 + k * 3, dx = 342 + k * 7; line(p, legX(ay) + 0.5, ay, dx, deckY(dx), '#a8a4c0', 120); }
  for (let k = 0; k < 5; k++) { const ay = 116 + k * 3, dx = 410 + k * 8; line(p, legX(ay, true), ay, dx, deckY(dx), '#a8a4c0', 110); }
  for (let y = 111; y <= BS.foot; y++) { const x = Math.round(legX(y, true)); put(x, y, '#b4b0c0'); put(x + 1, y, '#7a768c'); }   // the far leg
  for (let x = BS.deckL; x < W; x++) {             // the deck: lamps along its edge, its dark side below
    const y = deckY(x);
    put(x, y - 1, x % 5 === 0 ? '#ffe2a8' : null);
    put(x, y, '#cfc6c0'); put(x, y + 1, '#3a3446'); put(x, y + 2, '#22202e');
  }
  for (let y = 111; y <= BS.foot; y++) { const x = Math.round(legX(y)); put(x, y, '#fbf8f2'); put(x + 1, y, '#d2ccd6'); put(x + 2, y, '#8e88a0'); }   // the near leg
  for (const [x, y, c] of [[399, 108, '#ffffff'], [400, 108, '#ffffff'], [398, 109, '#fbf8f2'], [401, 109, '#e8e4ec'], [402, 110, '#b4b0c0'], [397, 110, '#fbf8f2']]) put(x, y, c);
  return p.done();
}
// the Thu Thiem bank north of us, where the pylon stands: a dark strip of park trees and lamps beyond the water
function renderNorthBank() {
  const p = painter(W, H), x0 = 370;
  for (let x = x0; x < W; x++) {
    const top = 157 - Math.round(1.5 + 1.5 * Math.sin(x * 0.7) + hash(x, 3) * 1.5);
    for (let y = top; y <= 163; y++) p.put(x, y, y === 163 ? '#2a2a40' : y < 158 ? '#121c22' : '#0e1220');
    if (x % 9 === 4) { p.put(x, 160, '#ffd890'); p.put(x, 161, '#5a4a40'); }
  }
  for (let y = 158; y <= 163; y++) for (let x = PYLON_FOOT - 3; x <= PYLON_FOOT + 8; x++) p.put(x, y, y === 158 ? '#8a8494' : '#3a3644');   // the pylon's pier
  return p.done();
}

/* ================= The District 1 riverfront: Ton Duc Thang's buildings, trees, lamps and Bach Dang quay ================= */
// [x0, x1, roof, kind]: 'low' riverfront offices and hotels, 'blk' the block with the rooftop sign, 'maj' the Majestic
const FRONT = [
  [0, 22, 140, 'low'], [22, 46, 135, 'low'], [46, 70, 142, 'low'], [70, 96, 134, 'low'], [96, 120, 140, 'low'],
  [120, 140, 137, 'low'], [140, 160, 141, 'low'], [160, BLOCK.x0, 139, 'low'], [BLOCK.x0, BLOCK.x1, BLOCK.roof, 'blk'],
  [BLOCK.x1, 244, 141, 'low'], [244, 272, 139, 'maj'], [272, 292, 141, 'low'], [292, 312, 136, 'low'], [312, 334, 140, 'low'],
  [334, 352, 143, 'low'], [352, 372, 139, 'low'],
];
const ROAD = 150;                                  // Ton Duc Thang, under the riverside trees
const QUAY = 155;                                  // Bach Dang quay's wall, down to the water at HZ
function renderFront() {
  const p = painter(W, H), put = p.put;
  for (const [x0, x1, roof, kind] of FRONT) {
    if (kind === 'maj') {                          // the Majestic (1925): cream, floodlit, a corner turret on Dong Khoi
      for (let y = roof; y < ROAD; y++) for (let x = x0; x < x1; x++) {
        let col = mixHex('#c8b48a', '#f2e2bc', (y - roof) / (ROAD - roof));
        if (y === roof + 1) col = '#fff4d8';
        else if (y > roof + 2 && (y - roof) % 3 !== 0 && (x - x0) % 3 === 1) col = hash(x, y) < 0.7 ? '#ffd890' : '#6a4a30';
        put(x, y, col);
      }
      for (let y = roof - 5; y < roof; y++) for (let x = x0; x < x0 + 5; x++) put(x, y, y === roof - 5 ? (x === x0 + 2 ? '#fff4d8' : null) : y === roof - 4 ? (x > x0 && x < x0 + 4 ? '#e8d4a8' : null) : '#dcc89c');
      continue;
    }
    const r = rng(x0 * 7 + roof), blk = kind === 'blk';
    for (let y = roof; y < ROAD; y++) for (let x = x0; x < x1; x++) {
      let col = blk ? '#1a1c30' : '#1c1828';
      if (x === x0) col = blk ? '#2c3050' : '#2a2436'; else if (x === x1 - 1) col = '#120f1c';
      if (y === roof) col = blk ? '#4a5070' : '#3a3246';
      else if (y > roof + 1 && (y - roof) % 2 === 0 && x > x0 && x < x1 - 1) {
        const h = hash(x0, x, y);
        if (blk ? (x - x0) % 3 !== 0 && h < 0.6 : (x - x0) % 2 === 1 && h < 0.42) col = h < 0.2 ? '#e8ecf4' : '#ffcf86';
      }
      put(x, y, col);
    }
    if (r() < 0.4 && !blk) put(x0 + 2 + Math.floor(r() * (x1 - x0 - 4)), roof - 1, '#ff4a3a');   // a rooftop aviation lamp
  }
  // the riverside trees along Ton Duc Thang, lit from below by the street lamps
  for (let x = 0; x < 372; x++) {
    const h = hash(Math.floor(x / 9), 11), cxT = Math.floor(x / 9) * 9 + 4.5, rad = 3.5 + h * 2;
    if (h < 0.18) continue;
    const top = Math.round(ROAD - 2 - Math.sqrt(Math.max(0, rad * rad - (x - cxT) ** 2)) * 1.4);
    for (let y = top; y < ROAD; y++) put(x, y, y >= ROAD - 2 ? '#3a3a26' : y === top ? '#22342a' : '#0e1a18');
  }
  for (let x = 0; x < 372; x++) {                  // the road, the riverside walk and its lamps, the quay wall
    put(x, ROAD, '#141220'); put(x, ROAD + 1, '#1a1826'); put(x, ROAD + 2, '#141220');
    put(x, ROAD + 3, '#2a2432'); put(x, ROAD + 4, '#3a3040');
    for (let y = QUAY; y < HZ; y++) put(x, y, y === QUAY ? '#6a5a5a' : '#2a2232');
    if (x % 10 === 3) { put(x, ROAD + 2, '#ffe0a0'); put(x, ROAD + 3, '#4a3e40'); put(x, QUAY, '#a08a70'); }
  }
  return p.done();
}
// the corner's sign: a lightbox in a dark steel frame, hung on two chains from the tamarind's limb
function renderBillboardFrame() {
  const p = painter(W, H), put = p.put;
  for (let y = BB.y - 1; y <= BB.y + BB.h; y++) for (let x = BB.x - 1; x <= BB.x + BB.w; x++) put(x, y, y === BB.y - 1 ? '#5a5466' : y === BB.y + BB.h ? '#141018' : '#2a2634');
  for (const cx of [BB.x + 5, BB.x + BB.w - 6]) for (let y = limbY(cx) + 2; y < BB.y - 1; y++) put(cx, y, y % 2 ? '#6a6470' : '#3a3644');
  return p.done();
}
// the city's own ad: a glass of cà phê sữa đá under its phin filter, "CÀ PHÊ / SÀI GÒN"
function renderBillboardAd() {
  const p = painter(BB.w, BB.h), put = p.put;
  for (let y = 0; y < BB.h; y++) for (let x = 0; x < BB.w; x++) put(x, y, ramp(['#3a0e1c', '#4a1222', '#5a1828', '#6a1e2e'], 1 - y / BB.h * 0.9, x, y));
  const ph = [[5, 2, 5, '#d8d8e0'], [4, 3, 7, '#b8b8c4'], [4, 4, 7, '#a8a8b8'], [4, 5, 7, '#a8a8b8'], [4, 6, 7, '#9898a8'], [3, 7, 9, '#c8c8d4']];
  for (const [x0, y, w, c] of ph) for (let x = x0; x < x0 + w; x++) put(x, y, x === x0 + w - 1 ? '#6a6a7a' : c);
  for (let y = 8; y <= 16; y++) for (let x = 4; x <= 9; x++) {
    let c = y < 13 ? '#3a2014' : '#f2e8d4';
    if (y === 12) c = '#8a5a3a';
    if (x === 4 || x === 9) c = '#c8d4e0';
    if (y === 16) c = '#c8d4e0';
    if (y < 10 && x > 4 && x < 9) c = '#5a3424';
    put(x, y, c);
  }
  put(7, 1, '#f0e0c0');
  const c = p.done(), x = c.getContext('2d');
  sign(x, 'CÀ PHÊ', 15, 4, '#f6e8c8');             // marks in rows 1-2, letters 4-8
  sign(x, 'SÀI GÒN', 15, 13, '#ff7a96');            // marks in rows 10-11, letters 13-17
  return gradeAd(c, { level: 0.86, tint: '#141834', mix: 0.1, wear: 0.2, seed: 7 });
}

/* ================= The Saigon River ================= */
const WATER_PAL = ['#05071a', '#080b20', '#0b0e27', '#10122e', '#161634', '#1e1a3c', '#281f44', '#33244a'];
function renderWater() {
  return ditherFill(W, EDGE - HZ, WATER_PAL, (i, y) => {
    let v = 0.55 * (1 - y / (EDGE - HZ));
    v += Math.max(0, 1 - Math.hypot((i - 230) / 260, y / 26)) * 0.4;   // the city's glow lying on the far water
    return v;
  });
}
// the dinner boats moored along Bach Dang quay: two decks lit warm, a string of coloured bulbs along the top
const MOORED = [{ x: 74, w: 38 }, { x: 300, w: 34 }];
function renderMoored(B) {
  const p = painter(B.w, 10), put = p.put, w = B.w;
  for (let x = 0; x < w; x++) {
    const bow = x < 4 ? 4 - x : 0;
    for (let y = 6 + (bow >> 1); y < 10; y++) put(x, y, y === 6 ? '#e8e0d0' : y === 7 && x % 2 ? '#ffd890' : '#1a1626');
    if (x > 3 && x < w - 2) for (let y = 3; y < 6; y++) put(x, y, y === 3 ? '#d8d0c4' : x % 3 ? '#ffe2a6' : '#5a4436');
    if (x > 8 && x < w - 5) for (let y = 1; y < 3; y++) put(x, y, y === 1 ? '#c8c0b8' : x % 4 ? '#fff0c8' : '#4a3a34');
  }
  return p.done();
}
const BULBS = ['#ff5a6a', '#ffd04a', '#5ad0ff', '#7aff8a', '#ff9aea'];
function drawMoored(t) {
  MOORED.forEach((B, i) => {
    ctx.drawImage(MOORED_C[i], B.x, HZ - 10);
    for (let x = B.x + 2; x < B.x + B.w - 2; x += 2) R(x, HZ - 11 + ((x >> 1) % 2), 1, 1, BULBS[(x / 2 + step(t, 160)) % BULBS.length | 0]);
    halo(HL.boat, B.x + (B.w >> 1), HZ - 6, 0.8);
  });
}
// a dinner cruise heading downstream (right to left) across most of the loop, and the waterbus going upstream
const CRUISE = { t0: 24, t1: 204, wl: 176, len: 46 };
const WATERBUS = { t0: 112, t1: 192, wl: 166, len: 26 };
function renderCruise() {
  const p = painter(CRUISE.len, 16), put = p.put, w = CRUISE.len;
  for (let x = 0; x < w; x++) {
    const bow = x < 6 ? 6 - x : 0, stern = x > w - 3 ? x - (w - 3) : 0;
    for (let y = 10 + (bow >> 1); y < 16 - stern; y++) put(x, y, y === 10 + (bow >> 1) ? '#f0e8dc' : y === 12 && x % 2 ? '#ffd890' : y > 13 ? '#120f1c' : '#2a2438');
    if (x > 5 && x < w - 3) for (let y = 6; y < 10; y++) put(x, y, y === 6 ? '#e8e0d4' : (x % 3 && y < 9) ? '#ffe6b0' : '#4a3a36');
    if (x > 11 && x < w - 8) for (let y = 3; y < 6; y++) put(x, y, y === 3 ? '#dcd4cc' : x % 4 ? '#fff2d0' : '#3e3434');
  }
  for (let y = 0; y < 3; y++) put(w - 12, y, '#6a6070');   // the mast at the stern
  return p.done();
}
// the Saigon Waterbus: a white catamaran with a blue band and a row of cool-lit windows, bow to the right
function renderWaterbus() {
  const w = WATERBUS.len, p = painter(w, 9), put = p.put;
  for (let x = 0; x < w; x++) {
    const bow = x > w - 5 ? x - (w - 5) : 0;
    for (let y = 5 + (bow >> 1); y < 9; y++) put(x, y, y === 6 ? '#2a6ac8' : y > 7 ? '#141020' : '#e8ecf0');
    if (x > 2 && x < w - 5) for (let y = 1; y < 5; y++) put(x, y, y === 1 ? '#3a7ad8' : y === 2 ? '#d8e0ea' : x % 3 ? '#cfe6ff' : '#4a5a6a');
  }
  return p.done();
}
function drawBoat(c, x, wl, h, t, rows) {           // a hull's broken mirror under it, then the boat
  ctx.globalAlpha = 0.35;
  for (let k = 0; k < rows; k++) {
    const off = Math.round(Math.sin(TAU * 40 * t / LOOP + k * 0.9) * (0.4 + k * 0.15));
    ctx.drawImage(c, 0, h - 1 - k, c.width, 1, x + off, wl + 1 + k, c.width, 1);
  }
  ctx.globalAlpha = 1;
  ctx.drawImage(c, x, wl - h + 1);
}
function drawCruise(t) {
  if (t < CRUISE.t0 || t > CRUISE.t1) return;
  const u = (t - CRUISE.t0) / (CRUISE.t1 - CRUISE.t0), x = Math.round(lerp(W + 4, -CRUISE.len - 4, u)), y = CRUISE.wl - 15;
  drawBoat(CRUISE_C, x, CRUISE.wl, 16, t, 12);
  for (let k = 0; k < 18; k++) R(x + 12 + k * 2, y + 2, 1, 1, BULBS[(k + step(t, 240)) % BULBS.length]);
  halo(HL.boat, x + 22, y + 8, 0.9); halo(HL.boat, x + 34, y + 8, 0.7);
  R(x + CRUISE.len - 12, y - 1, 1, 1, frac(t) < 0.5 ? '#ff4a3a' : '#7a2a2a');
}
function drawWaterbus(t) {
  if (t < WATERBUS.t0 || t > WATERBUS.t1) return;
  const u = (t - WATERBUS.t0) / (WATERBUS.t1 - WATERBUS.t0), x = Math.round(lerp(-WATERBUS.len - 4, W + 4, u));
  drawBoat(WATERBUS_C, x, WATERBUS.wl, 9, t, 6);
  halo(HL.cool, x + 12, WATERBUS.wl - 5, 0.6);
  R(x + WATERBUS.len - 1, WATERBUS.wl - 4, 1, 1, '#5aff7a');   // starboard light, facing us as she heads upstream
}
// clumps of water hyacinth drifting downstream, right to left
const HYACINTH = [0, 1, 2, 3, 4, 5, 6, 7].map(i => ({ y: 163 + ((i * 7) % 28), ph: hash(i, 51), w: 4 + (i % 3) * 2, k: 1 + (i % 2) }));
function drawHyacinths(t) {
  for (const h of HYACINTH) {
    const x = Math.round(W + 10 - frac(h.k * t / LOOP + h.ph) * (W + 30));
    R(x, h.y, h.w, 1, '#2a4a2a'); R(x + 1, h.y - 1, h.w - 2, 1, '#3e6a38'); R(x - 1, h.y + 1, h.w + 2, 1, '#0e1a16');
    if (h.w > 5) R(x + 2, h.y - 2, 1, 1, '#c8a8e8');
  }
}

/* ================= Our bank: the railing, the wet promenade, its puddles, the lamps, the hedge ================= */
const NEAR = 207;                                  // the wet paving mirrors everything above this row
const LAWN = 263;                                  // the hedge along the bottom edge
const PUDDLES = [[150, 214, 70, 7], [224, 221, 56, 7], [312, 228, 96, 10], [410, 214, 64, 7], [92, 234, 76, 8], [252, 244, 84, 7], [436, 242, 60, 8], [28, 223, 48, 7], [372, 254, 80, 6], [150, 255, 70, 5], [40, 258, 50, 5]];
const pave = (x, y) => {
  const u = (y - PROM) / (H - PROM), joint = (y - PROM) % 9 === 0 || (x + ((y - PROM) / 9 | 0) * 7) % 16 === 0;
  return joint ? '#0e0c16' : ramp(['#100e18', '#16141f', '#1c1a27', '#232030'], 0.85 - u * 0.7, x, y);
};
const pool = (x, y) => Math.max(0, 1 - Math.hypot((x - 70) / 110, (y - 205) / 24));   // the corner's light on the paving
function renderBank() {
  const p = painter(W, H), put = p.put;
  for (let y = PROM; y < H; y++) for (let x = 0; x < W; x++) {
    const c = pave(x, y), l = pool(x, y);
    put(x, y, l > 0.1 ? mixHex(c, '#5a3a22', Math.min(0.55, l * 0.7)) : c);
  }
  for (let x = 0; x < W; x++) {
    put(x, EDGE, '#5a5266'); put(x, EDGE + 1, '#3a3446');
    put(x, RAIL, '#e8e4ec'); put(x, RAIL + 1, '#8a8698'); put(x, RAIL + 7, '#bcb8c8');
    if (x % 12 === 6) for (let y = RAIL + 1; y < EDGE; y++) { put(x, y, '#cac6d4'); put(x + 1, y, '#5a566a'); }
  }
  return p.done();
}
// the same paving again, laid over the mirror: clear where the puddles lie, thin at their edges, nearly opaque elsewhere
function renderDry() {
  const p = painter(W, H);
  for (let y = NEAR; y < LAWN; y++) for (let x = 0; x < W; x++) {
    let v = 0;
    for (const [cx, cy, rx, ry] of PUDDLES) v = Math.max(v, 1 - ((x - cx) / rx) ** 2 - ((y - cy) / ry) ** 2);
    if (v > 0.25) continue;
    const c = pave(x, y), l = pool(x, y), col = l > 0.1 ? mixHex(c, '#5a3a22', Math.min(0.55, l * 0.7)) : c;
    p.put(x, y, col, v > 0 ? 96 : 156);
  }
  return p.done();
}
// a low hedge of ixora along the bottom, its red flower heads catching the lamps
function renderHedge() {
  const p = painter(W, H);
  for (let x = 0; x < W; x++) {
    const top = LAWN - Math.round(2 + 2 * Math.sin(x * 0.21) + 1.5 * Math.sin(x * 0.53 + 1) + hash(x, 6) * 2);
    for (let y = top; y < H; y++) {
      let c = ramp(['#050a08', '#08100c', '#0c1610', '#122016'], 0.75 - (y - top) / 14 + hash(x, y) * 0.3, x, y);
      const fl = hash(x >> 2, 4), fx = (x & 3), fy = y - top - 1;
      if (fl > 0.8 && fy >= 0 && fy < 2 && fx > 0 && fx < 3) c = (fx + fy) % 2 ? '#8a2228' : '#c84038';   // a head of ixora flowers
      p.put(x, y, c);
    }
  }
  return p.done();
}
function renderLamp(x) {
  const p = painter(W, H), put = p.put;
  for (let y = LAMP_Y + 2; y < PROM + 1; y++) { put(x, y, '#5a5a6a'); put(x + 1, y, '#26262e'); }
  for (let y = PROM - 3; y < PROM + 1; y++) for (let i = x - 1; i <= x + 2; i++) put(i, y, i === x - 1 ? '#4a4a58' : '#22222a');
  for (let i = x - 4; i <= x + 5; i++) put(i, LAMP_Y + 2, '#3a3a46');
  for (const gx of [x - 4, x + 4]) for (let y = LAMP_Y - 1; y <= LAMP_Y + 1; y++) for (let i = gx; i <= gx + 2; i++)
    put(i, y, y === LAMP_Y - 1 && i !== gx + 1 ? null : i === gx + 2 ? '#e8c890' : '#fff6e0');
  put(x, LAMP_Y - 1, '#3a3a46'); put(x + 1, LAMP_Y - 1, '#3a3a46'); put(x, LAMP_Y - 2, '#5a5a6a');
  return p.done();
}

/* ================= The street-food corner, near and big on the left ================= */
// Under a tamarind: a bánh mì cart with its bare bulb, the vendor's charcoal brazier, two low plastic tables with their
// stools, a string of bulbs from the trunk to a pole, parked scooters. Its people stand and sit at 31 px (row 204-205).
const CART = { x0: 16, x1: 66, feet: 204 };       // 2.1 m with its board, 2.2 m tall
const BRAZIER = { x0: 86, x1: 96, top: 188 };
const TABLES = [[108, 126], [156, 174]];
const STOOLS = [[102, '#e03a3a', '#9a2222'], [132, '#3a7ae0', '#2a4ea0'], [150, '#e03a3a', '#9a2222'], [180, '#3a7ae0', '#2a4ea0']];
const FEET_S = 205;                                // the row the stools, the tables' people and the scooters stand on
const BULBS_C = [{ x: 40, y: 143 }, { x: 117, y: 150 }];   // bare bulbs on flexes from the limb: over the cart, over the first table
const BULB = BULBS_C[0];
const LIMB_END = 120;                              // where the limb ends (the shelter's edge)
const PARKED = { x: 190 };                         // the scooter parked for good (a cat asleep on its seat)
const SLOT = 230;                                  // where the rider parks
const LANE_B = 274;                                // the promenade's near lane starts past the corner
function scooterSprite(body, shade, flip) {
  const w = 36, h = 22, p = painter(w, h), put = (x, y, c) => p.put(flip ? w - 1 - x : x, y, c);
  for (const cx of [6, 28]) {                      // the wheels, then the body over the rear one
    for (let a = 0; a < 40; a++) {
      const g = a / 40 * TAU;
      put(cx + Math.round(Math.cos(g) * 5), 16 + Math.round(Math.sin(g) * 5), '#121014');
      put(cx + Math.round(Math.cos(g) * 4), 16 + Math.round(Math.sin(g) * 4), '#2a2830');
    }
    put(cx, 16, '#9a9aa4'); put(cx - 1, 16, '#5a5a64'); put(cx + 1, 16, '#5a5a64'); put(cx, 15, '#5a5a64'); put(cx, 17, '#5a5a64');
  }
  for (let k = 0; k <= 12; k++) put(Math.round(lerp(7, 10, k / 12)), Math.round(lerp(16, 4, k / 12)), '#3a3a44');   // the fork
  for (let y = 4; y < 14; y++) for (let x = 9; x < 14; x++) put(x, y, x === 9 ? mixHex(body, '#ffffff', 0.35) : x === 13 ? shade : body);   // the leg shield
  put(8, 5, '#d8d8d0'); put(8, 6, '#a8a8a0');      // the headlamp
  for (let x = 6; x < 14; x++) put(x, 2, '#2a2a32');
  put(7, 0, '#9aa0b0'); put(7, 1, '#3a3840');
  for (let x = 13; x < 20; x++) { put(x, 13, '#2a2a32'); put(x, 14, '#1e1e26'); }
  for (let y = 7; y < 16; y++) for (let x = 18; x < 35; x++) {
    if ((y === 7 || y === 15) && (x === 18 || x === 34)) continue;
    put(x, y, y === 15 ? shade : y === 11 ? mixHex(body, '#ffffff', 0.4) : x === 34 ? shade : body);
  }
  for (let x = 18; x < 32; x++) { put(x, 5, '#3a3038'); put(x, 6, '#1a1418'); }
  for (let x = 24; x < 33; x++) put(x, 18, '#7a7a84');   // the silencer
  put(34, 9, '#a01818'); put(34, 10, '#c02020');
  return p.done();
}
function renderCorner() {
  const p = painter(W, H), put = p.put;
  // the tamarind's trunk, flaring into roots on the paving
  for (let y = 92; y <= TREE.base; y++) {
    const half = 4.5 + (y - 92) / (TREE.base - 92) * 2 + (y > TREE.base - 7 ? (y - TREE.base + 7) * 0.9 : 0);
    for (let x = Math.round(TREE.x - half); x <= Math.round(TREE.x + half); x++) {
      const s = (x - TREE.x + half) / (2 * half), streak = hash(x, y >> 2) < 0.3;
      put(x, y, s > 0.78 ? (streak ? '#4a3426' : '#6a4a32') : s < 0.2 ? '#120c0c' : streak ? '#2a1e1a' : '#3a2a22');
    }
  }
  for (const b of BULBS_C) for (let y = limbY(b.x) + 2; y < b.y; y++) put(b.x, y, '#2a2a30');   // the bulbs' flexes
  // the cart: spoked wheels, the steel cabinet with its red stripe, the counter, the lit glass case of loaves, the board
  const { x0, x1, feet } = CART, wy = feet - 6;
  for (const wx of [x0 + 9, x1 - 9]) {
    for (let a = 0; a < 48; a++) {
      const g = a / 48 * TAU;
      put(wx + Math.round(Math.cos(g) * 6), wy + Math.round(Math.sin(g) * 6), '#121016');
      put(wx + Math.round(Math.cos(g) * 5), wy + Math.round(Math.sin(g) * 5), '#2a2830');
    }
    for (let k = 0; k < 8; k++) { const g = k / 8 * TAU + 0.3; for (let r = 1; r < 5; r++) put(wx + Math.round(Math.cos(g) * r), wy + Math.round(Math.sin(g) * r), '#5a5a66'); }
    put(wx, wy, '#a8a8b4');
  }
  for (let x = x0 + 2; x < x1 - 2; x++) put(x, feet - 12, '#2a2a34');
  for (let y = feet - 24; y < feet - 12; y++) for (let x = x0 + 1; x < x1 - 1; x++) {
    let c = (x - x0) % 16 === 1 ? '#5a7488' : '#7c98ac';
    if (x === x0 + 1) c = '#a8c0d0'; else if (x === x1 - 2) c = '#3e5668';
    if (y === feet - 24) c = '#c8dce8'; else if (y === feet - 20) c = '#c8303a'; else if (y === feet - 13) c = '#4a5e70';
    put(x, y, c);
  }
  for (let x = x0 - 2; x <= x1 + 1; x++) { put(x, feet - 25, '#eef2f4'); put(x, feet - 24, x < x0 + 1 || x > x1 - 2 ? '#8a9aa8' : null); }
  for (let y = feet - 38; y < feet - 25; y++) for (let x = x0 + 2; x < x1 - 2; x++) {
    let c = y < feet - 32 ? '#fff8e4' : '#fff2d4';
    if (x === x0 + 2 || x === x1 - 3 || y === feet - 38) c = '#d8e2ea'; else if (y === feet - 33) c = '#d8d0c0';
    put(x, y, c);
  }
  for (const sy of [feet - 36, feet - 31]) for (let lx = x0 + 4; lx < x1 - 10; lx += 9) for (let x = lx; x < lx + 7; x++) {
    const end = x === lx || x === lx + 6;
    put(x, sy, end ? '#b87430' : '#f2c472'); put(x, sy + 1, end ? '#8a5020' : '#d8963e');
  }
  for (let x = x0 + 4; x < x1 - 4; x++) put(x, feet - 28, x % 3 === 0 ? '#4a8a3a' : x % 7 === 2 ? '#d83a2a' : x % 5 === 1 ? '#c8b8a0' : null);
  for (const px of [x0 + 3, x1 - 4]) for (let y = feet - 42; y < feet - 38; y++) put(px, y, '#8a8a94');
  for (let y = feet - 55; y < feet - 42; y++) for (let x = x0; x < x1; x++) put(x, y, y === feet - 55 || y === feet - 43 || x === x0 || x === x1 - 1 ? '#f0dcb4' : '#b81e2a');
  for (let y = feet - 30; y < feet - 25; y++) { put(x1 - 1, y, '#e8e0d0'); put(x1, y, '#d8d0c0'); }   // paper bags at the end of the counter
  // the brazier: a clay pot on a stand, its grate of skewers
  for (const lx of [BRAZIER.x0 + 1, BRAZIER.x1 - 1]) for (let y = 198; y <= FEET_S; y++) put(lx, y, '#2a2a30');
  for (let y = BRAZIER.top + 1; y < 198; y++) for (let x = BRAZIER.x0 + (y > 195 ? 1 : 0); x <= BRAZIER.x1 - (y > 195 ? 1 : 0); x++)
    put(x, y, y === BRAZIER.top + 1 ? '#5a3020' : x === BRAZIER.x0 + (y > 195 ? 1 : 0) ? '#b06a3a' : '#8a4a2a');
  for (let x = BRAZIER.x0; x <= BRAZIER.x1; x++) put(x, BRAZIER.top, x % 2 ? '#4a4a52' : null);
  // the low tables (glasses of iced tea, a plate) and the stools
  TABLES.forEach(([a, b], i) => {
    for (let x = a; x <= b; x++) { put(x, 192, '#4a9af0'); put(x, 193, '#2a6ab0'); }
    for (const lx of [a + 1, b - 1]) for (let y = 194; y < FEET_S; y++) put(lx, y, '#2a5a9a');
    for (let x = a + 2; x < b - 1; x++) put(x, 200, '#2a5a9a');
    for (const gx of [a + 3, b - 4]) { put(gx, 189, '#f0f0f0'); put(gx, 190, '#d89a3a'); put(gx, 191, '#b87a2a'); put(gx + 1, 190, '#c88a30'); put(gx + 1, 191, '#a86a20'); }
    if (!i) for (let x = a + 7; x < a + 12; x++) put(x, 191, '#e8e8e8');
  });
  for (const [sx, c, d] of STOOLS) for (let y = 198; y <= FEET_S; y++) for (let x = sx - 4; x <= sx + 4; x++)
    if (y < 200 || x === sx - 4 + ((y - 200) >> 2) || x === sx + 4 - ((y - 200) >> 2)) put(x, y, y === 198 ? mixHex(c, '#ffffff', 0.25) : y === 199 ? c : d);
  return p.done();
}
// the canopy: a broad tamarind crown of feathery leaves, the sky showing through, lit warm from below by the bulbs; its
// branches show in the gaps, and the bare limb the sign hangs from runs out to the right
function renderCanopy(sway) {
  const p = painter(W, H), put = p.put, r = rng(77), dx = y => Math.round(sway * 2 * clamp(1 - y / 104, 0, 1));
  const top = x => 6 + 0.0055 * (x - 36) ** 2, bot = x => x < 56 ? 108 + 3 * Math.sin(x * 0.35) : 96 - (x - 56) * 0.06;
  const clusters = [];
  while (clusters.length < 110) {
    const x = -12 + r() * 138, y = top(x) + r() * (bot(x) - top(x));
    if (x > 118 && y > 70) continue;
    clusters.push({ x, y, rx: 6 + r() * 7, ry: 4 + r() * 3 });
  }
  for (let y = 0; y < 116; y++) for (let x = 0; x < 134; x++) {
    const sx = x - dx(y);                          // the crown sways with the breeze, its top the most
    let v = 0;
    for (const c of clusters) v = Math.max(v, 1 - ((sx - c.x) / c.rx) ** 2 - ((y - c.y) / c.ry) ** 2);
    if (v <= 0) continue;
    if (hash(sx, y, 3) < 0.03 + (1 - v) * 0.32) continue;                    // feathery: small gaps, more at the edges
    if (v < 0.5 && hash(sx >> 3, y >> 3, 11) < 0.1) continue;                // and here and there a hole of sky
    const lit = Math.max(0, 1 - Math.hypot((x - 64) / 80, (y - 132) / 44)), leaf = hash(sx, y, 9);
    let c = leaf < 0.3 ? '#08100c' : leaf < 0.7 ? '#0d1810' : '#132216';
    if (y < top(x) + 6 && leaf > 0.6) c = '#1c2c2a';                          // the top catching the sky's glow
    if (lit > 0.25 && leaf > 0.4) c = lit > 0.45 ? (leaf > 0.75 ? '#7a6a30' : '#4e4a26') : '#2a3220';
    put(x, y, c);
  }
  const branch = (x0, y0, x1, y1, w) => {
    const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
    for (let k = 0; k <= n; k++) {
      const x = Math.round(lerp(x0, x1, k / n)), y = Math.round(lerp(y0, y1, k / n)), ww = Math.max(1, Math.round(lerp(w, 1, k / n)));
      for (let j = 0; j < ww; j++) if (!p.get(x, y + j) || y + j >= TREE.limb - 1) put(x, y + j, j === 0 ? '#4a3628' : '#24181a');
    }
  };
  branch(TREE.x - 2, 96, -6, 58, 3); branch(TREE.x, 94, 30, 44, 3); branch(TREE.x + 2, 98, 66, 62, 3);
  branch(40, TREE.limb - 1, 74, 74, 2); branch(84, TREE.limb - 2, 106, 80, 1);
  for (let x = TREE.x + 3; x <= LIMB_END; x++) {   // the limb, thick by the trunk, thinning out
    const y = limbY(x), th = x < 50 ? 4 : x < 90 ? 3 : 2;
    for (let j = 0; j < th; j++) put(x, y + j, j === 0 ? '#6a4a34' : j === th - 1 ? '#1a1214' : '#3a2a22');
  }
  return p.done();
}

/* ================= People ================= */
// One function draws everyone. x: the body's centre column, y: the row of the shoes. L: a look { skin, hair, top,
// bottom, shoe, long, skirt, cap }. o: { h (standing height), view 'front' | 'back' | 'side', dir (side: 1 faces right),
// pose 'stand' | 'walk' | 'sit', stride (walk: 0 legs apart, 1 together), seat (sit: rows from the shoes up to the
// seat), arm 'down' | 'phone' | 'mouth' | 'cup' | 'out' | 'work' | 'fan' | 'up', a (the arm's phase 0..1), lean (the
// head's lean, -1..1), rim (a row of the corner's light along hair and shoulders), food (a bánh mì in hand) }
const EYE_C = '#2a1a18';
function person(x, y, L, o) {
  const h = o.h, view = o.view || 'front', dir = o.dir || 1, pose = o.pose || 'stand', arm = o.arm || 'down', side = view === 'side';
  const big = h >= 34, hw = big ? 5 : 4, tw = side ? (big ? 5 : 4) : (big ? 7 : 6), tx = x - (tw >> 1), hx = x + (o.lean || 0), hl = hx - 2;
  const tH = Math.round(h * 0.31), legH = h - 7 - tH, sit = pose === 'sit', seat = o.seat || 7, shoe = L.shoe || '#18141a';
  const tb = sit ? y - seat - 1 : y - legH - 1, ts = tb - tH + 1, top = ts - 6;
  const sleeve = mixHex(L.top, '#000000', 0.18), shade = mixHex(L.top, '#000000', 0.32), lx = tx, rx = tx + tw - 2;
  // legs first, so the torso and the near arm sit over them
  if (sit) {
    const hr = y - seat, th = Math.round(h * 0.19), kx = x + dir * th;
    if (side) {
      R(Math.min(x - 1, kx - 1), hr - 1, th + 3, 2, L.bottom);
      R(dir > 0 ? kx - 1 : kx, hr + 1, 2, y - hr - 1, L.bottom);
      R(kx - 1, y, 3, 1, shoe);
    } else {
      R(tx - 1, hr - 1, tw + 2, 2, L.bottom);
      R(lx, hr + 1, 2, y - hr - 1, L.bottom); R(rx, hr + 1, 2, y - hr - 1, L.bottom);
      R(lx, y, 2, 1, shoe); R(rx, y, 2, 1, shoe);
    }
  } else if (side) {
    R(x - 1, tb + 1, 3, 2, L.bottom);
    if (pose === 'walk' && o.stride === 0) {
      const n = y - tb - 3, sp = big ? 4 : 3;
      for (let r = 0; r < n; r++) {
        const f = Math.round(dir * (r + 1) / n * sp);
        R(x - 1 + f, tb + 3 + r, 2, 1, L.bottom); R(x - 1 - f, tb + 3 + r, 2, 1, mixHex(L.bottom, '#000000', 0.25));
      }
      R(x - 1 + dir * sp + (dir > 0 ? 0 : -1), y, 3, 1, shoe); R(x - 1 - dir * sp, y, 2, 1, shoe);
    } else {
      R(x - 1, tb + 3, 2, y - tb - 3, L.bottom);
      R(x - 1 + (dir > 0 ? 0 : -1), y, 3, 1, shoe);
    }
  } else if (L.skirt) {
    R(tx, tb + 1, tw, 6, L.bottom);
    R(lx + 1, tb + 7, 1, y - tb - 7, L.skin); R(rx, tb + 7, 1, y - tb - 7, L.skin);
    R(lx + 1, y, 2, 1, shoe); R(rx, y, 2, 1, shoe);
  } else {
    R(tx, tb + 1, tw, 2, L.bottom);
    R(lx, tb + 3, 2, y - tb - 3, L.bottom); R(rx, tb + 3, 2, y - tb - 3, L.bottom);
    if (rx - lx > 2) R(lx + 2, tb + 3, rx - lx - 2, 3, mixHex(L.bottom, '#000000', 0.3));
    R(lx, y, 2, 1, shoe); R(rx, y, 2, 1, shoe);
  }
  // torso, neck, head
  R(tx, ts, tw, tH, L.top); R(tx + tw - 1, ts, 1, tH, shade);
  if (o.rim) R(tx, ts, tw - 1, 1, mixHex(L.top, o.rim, 0.5));
  R(x - 1, top + 5, 2, 1, L.skin);
  R(hl, top, hw, 2, L.hair);
  if (side) {
    R(hl, top + 2, hw, 3, L.skin);
    R(dir > 0 ? hl : hl + hw - 2, top + 2, 2, 2, L.hair);
    R(dir > 0 ? hl + hw - 1 : hl, top + 2, 1, 1, EYE_C);
    R(dir > 0 ? hl + hw : hl - 1, top + 3, 1, 1, L.skin);
    if (L.long) R(dir > 0 ? hl : hl + hw - 1, top + 2, 1, 7, L.hair);
  } else {
    R(hl, top + 2, hw, 3, view === 'back' ? L.hair : L.skin);
    if (view === 'front') { R(hl, top + 2, 1, 2, L.hair); R(hl + hw - 1, top + 2, 1, 2, L.hair); R(hl + 1, top + 3, 1, 1, EYE_C); R(hl + hw - 2, top + 3, 1, 1, EYE_C); }
    if (L.long) { if (view === 'back') R(hl, top + 4, hw, 5, L.hair); else { R(hl, top + 4, 1, 4, L.hair); R(hl + hw - 1, top + 4, 1, 4, L.hair); } }
  }
  if (L.cap) { R(hl, top, hw, 1, L.cap); if (side) R(dir > 0 ? hl + hw : hl - 1, top + 1, 1, 1, L.cap); }
  if (o.rim) R(hl, top, hw, 1, mixHex(L.hair, o.rim, 0.55));
  // arms
  const a = o.a || 0;
  if (!side) {
    const down = ax => { R(ax, ts, 1, tH - 3, sleeve); R(ax, ts + tH - 3, 1, 2, L.skin); };
    down(tx - 1);
    if (arm === 'phone') { R(tx + tw, ts, 1, 3, sleeve); R(tx + tw - 1, ts - 1, 1, 2, L.skin); R(tx + tw - 1, ts - 4, 2, 3, '#c8e0ff'); }
    else if (arm === 'up') { R(tx + tw, ts - 5, 1, 6, sleeve); R(tx + tw, ts - 7, 1, 2, L.skin); }
    else down(tx + tw);
    return;
  }
  const sc = x + (dir > 0 ? 0 : -1), fx = d => sc + dir * d;
  if (arm === 'mouth' || arm === 'cup') {
    R(sc, ts, 1, 3, sleeve); R(fx(1), ts + 2, 1, 1, L.skin); R(fx(2), ts, 1, 2, L.skin); R(dir > 0 ? hl + hw : hl - 1, top + 4, 1, 1, L.skin);
    if (arm === 'cup') { R(fx(3), ts - 2, 1, 2, '#d89a3a'); R(fx(3), ts - 3, 1, 1, '#f0f0f0'); }
    else if (o.food) R(dir > 0 ? hl + hw + 1 : hl - 4, top + 4, 3, 1, '#e0a050');
  } else if (arm === 'out' || arm === 'work' || arm === 'fan') {
    const hy = arm === 'out' ? ts + 3 - Math.round(a * 2) : arm === 'work' ? ts + tH - 3 + Math.round(Math.sin(a * TAU)) : ts + 2 + Math.round(Math.sin(a * TAU) * 2);
    R(sc, ts, 1, 3, sleeve);
    for (let k = 1; k <= 4; k++) R(fx(k), Math.round(lerp(ts + 3, hy, k / 4)), 1, 1, k < 3 ? sleeve : L.skin);
    if (arm === 'fan') R(fx(5), hy - 1, 1, 3, '#c8a060');
    if (o.food) R(dir > 0 ? fx(4) : fx(6), hy, 3, 1, '#e0a050');
  } else {
    const sw = pose === 'walk' ? (o.stride === 0 ? dir : 0) : 0;
    R(sc, ts, 1, tH - 4, sleeve); R(sc - sw, ts + tH - 4, 1, 2, L.skin);
  }
}
// looks: skin tones, hair, tops and bottoms, mixed for the night by depth
const SKINS = ['#d8a07a', '#c4865e', '#e2b088', '#b07850'];
const HAIRS = ['#141018', '#1a1418', '#2a1e1a', '#3a2a22'];
const night = (L, f) => f ? Object.fromEntries(Object.entries(L).map(([k, v]) => [k, typeof v === 'string' && v[0] === '#' ? mixHex(v, '#141834', f) : v])) : L;
const look = (s, hr, top, bottom, extra = {}) => ({ skin: SKINS[s], hair: HAIRS[hr], top, bottom, ...extra });
const RIM = '#ffd8a0';                             // the corner's warm light on hair and shoulders

// the corner's people: the vendor between her cart and her brazier, two at each table (one of them the rider, below)
const VENDOR = { x: 76, y: CART.feet, L: look(0, 1, '#e8c8d0', '#3a3a5a', { long: true }) };
const SITTERS = [
  { id: 'eat', x: 102, dir: 1, L: look(1, 0, '#f0ece4', '#2a3a5a'), rank: 0.06 },
  { id: 'talk', x: 132, dir: -1, L: look(2, 1, '#3a8a6a', '#1e1e28', { long: true }), rank: 0.31 },
  { id: 'tea', x: 150, dir: 1, L: look(3, 3, '#8a8a92', '#4a3a2a'), rank: 0.19 },
];
function drawVendor(t) {
  const c = cycle(t, 12), atGrill = c.u > 0.7;                 // 14 s at the cart, 6 s fanning the coals
  person(VENDOR.x, VENDOR.y, VENDOR.L, { h: tall(VENDOR.y), view: 'side', dir: atGrill ? 1 : -1, arm: atGrill ? 'fan' : 'work', a: frac(t * (atGrill ? 2 : 1.5)), rim: RIM });
}
function drawSitter(s, t) {
  const L = s.L, base = { h: tall(FEET_S), view: 'side', dir: s.dir, pose: 'sit', seat: 7, rim: RIM, lean: frac(t / 9 + s.rank * 3) < 0.3 ? s.dir : 0 };
  if (s.id === 'eat') { const u = frac(t / 8 + 0.2); person(s.x, FEET_S, L, { ...base, arm: u < 0.35 ? 'mouth' : 'out', a: 0, food: true }); }
  else if (s.id === 'talk') { const u = frac(t / 6); person(s.x, FEET_S, L, { ...base, arm: u > 0.55 && u < 0.85 ? 'out' : 'down', a: frac(t * 1.5) > 0.5 ? 1 : 0 }); }
  else { const u = frac(t / 10 + 0.6); person(s.x, FEET_S, L, { ...base, arm: u < 0.25 ? 'cup' : 'down' }); }
}

// the rider: pulls up at the corner, sits down at the second table for a bánh mì and a talk, and rides off again
const RIDE = { in0: 96, in1: 104, walk0: 106, sit0: 110, sit1: 150, walk1: 154, out0: 156, out1: 166, stool: 180 };
const RIDER_L = look(1, 0, '#2a5a9a', '#2a2a34');
const HELMET = '#e8c040';
function drawRiding(x, dir, t) {
  ctx.drawImage(dir < 0 ? RIDE_L : RIDE_R, x, FEET_S - 21);
  const rx = dir < 0 ? x + 23 : x + 12, hlx = dir < 0 ? x + 8 : x + 27;
  const h = tall(FEET_S), top = FEET_S - 7 - 8 - 1 - Math.round(h * 0.31) + 1 - 6;
  person(rx, FEET_S - 7, RIDER_L, { h, view: 'side', dir, pose: 'sit', seat: 8, arm: 'out', a: 0 });
  R(rx - 2, top - 1, 6, 3, HELMET); R(rx + (dir > 0 ? 2 : -3), top + 1, 1, 1, '#8a6a20');
  R(hlx, FEET_S - 16, 1, 1, '#fff8e0'); halo(HL.head, hlx + dir * 2, FEET_S - 16, 0.8);
  R(dir < 0 ? x + 34 : x + 1, FEET_S - 12, 1, 2, '#ff3a2a');
}
function drawRider(t, DL) {
  const { in0, in1, walk0, sit0, sit1, walk1, out0, out1, stool } = RIDE;
  if (t < in0 || t > out1) return;
  if (t < in1) { const u = (t - in0) / (in1 - in0); DL.push([FEET_S + 0.5, () => drawRiding(Math.round(lerp(W + 10, SLOT, 1 - (1 - u) ** 2)), -1, t)]); return; }
  if (t >= out0) { const u = (t - out0) / (out1 - out0); DL.push([FEET_S + 0.5, () => drawRiding(Math.round(lerp(SLOT, W + 10, u * u)), 1, t)]); return; }
  DL.push([FEET_S - 0.2, () => {                   // the scooter at the slot, the helmet on its seat while he eats
    ctx.drawImage(RIDE_L, SLOT, FEET_S - 21);
    if (t >= walk0 && t < walk1) R(SLOT + 22, FEET_S - 18, 4, 2, HELMET);
  }]);
  const stand = (x, dir, walking) => () => {
    person(x, FEET_S, RIDER_L, { h: tall(FEET_S), view: 'side', dir, pose: walking ? 'walk' : 'stand', stride: Math.floor(x / 3) % 2 });
    if (t < walk0 || t >= walk1) R(x - 2, FEET_S - tall(FEET_S), 6, 3, HELMET);
  };
  if (t < walk0) DL.push([FEET_S + 0.5, stand(SLOT + 4, -1, false)]);
  else if (t < sit0) DL.push([FEET_S + 0.5, stand(Math.round(lerp(SLOT + 4, stool + 2, (t - walk0) / (sit0 - walk0))), -1, true)]);
  else if (t < sit1) DL.push([FEET_S, () => { const u = frac(t / 7); person(stool, FEET_S, RIDER_L, { h: tall(FEET_S), view: 'side', dir: -1, pose: 'sit', seat: 7, arm: u < 0.3 ? 'mouth' : u > 0.6 && u < 0.8 ? 'out' : 'down', food: true, rim: RIM }); }]);
  else if (t < walk1) DL.push([FEET_S + 0.5, stand(Math.round(lerp(stool + 2, SLOT + 4, (t - sit1) / (walk1 - sit1))), 1, true)]);
  else DL.push([FEET_S + 0.5, stand(SLOT + 4, 1, false)]);
}

// the promenade: the couple at the railing, someone filming the skyline, the LED-toy seller, the sweeper, the walkers
const COUPLE = { x: 290, y: 199, him: night(look(0, 0, '#f2f0ea', '#2a3a5a'), 0.12), her: night(look(2, 1, '#e89ab0', '#e8e4dc', { long: true }), 0.12), rank: 0.12 };
const FILMER = { x: 380, y: 200, L: night(look(1, 2, '#4a7a5a', '#3a3440'), 0.12), rank: 0.44 };
const TOYMAN = { x: 418, y: 201, L: night(look(3, 0, '#c8b040', '#2a2a34', { cap: '#3a3a4a' }), 0.1) };
const SWEEPER = { y: 205, L: look(0, 1, '#e8742a', '#2a3040', { cap: '#2a5a8a' }) };
const TOY_COLS = ['#5ad8ff', '#ff6ad8', '#8aff6a'];
function drawToys(t) {
  const { cyc, u } = cycle(t, 20, 0.3), d = (hash(cyc, 3) - 0.5) * 30, col = TOY_COLS[cyc % 3];
  let x, y, spin = step(t, 2400) % 2;
  if (u < 0.06) { x = lerp(TOYMAN.x + 1, TOYMAN.x - 4 + d * 0.3, u / 0.06); y = lerp(173, 102, Math.sqrt(u / 0.06)); spin = 1; }
  else if (u < 0.6) { const v = (u - 0.06) / 0.54; x = TOYMAN.x - 4 + d * (0.3 + 0.7 * v) + Math.sin(v * 18) * 3; y = lerp(102, 198, v); }
  else if (u < 0.86) { x = TOYMAN.x - 4 + d; y = 198; spin = 0; }
  else return;
  x = Math.round(x); y = Math.round(y);
  R(x - (spin ? 1 : 0), y, spin ? 3 : 1, 1, col); R(x, y, 1, 1, '#ffffff');
  halo(HL.toy, x, y, u < 0.6 ? 0.8 : 0.5);
}
function drawToyman(t) {
  const { u } = cycle(t, 20, 0.3);
  person(TOYMAN.x, TOYMAN.y, TOYMAN.L, { h: tall(TOYMAN.y), view: 'front', arm: u > 0.94 || u < 0.03 ? 'up' : 'down' });
  for (let k = 0; k < 3; k++) R(TOYMAN.x - 5 + k, TOYMAN.y - 13 - k, 1, 1, TOY_COLS[k]);   // the toys he holds
}
function drawSweeper(t) {
  const v = Math.sin(TAU * t / LOOP), x = Math.round(300 + 120 * (0.5 - 0.5 * Math.cos(TAU * t / LOOP))), dir = v >= 0 ? 1 : -1;
  const sweep = Math.round(3 * Math.sin(TAU * 160 * t / LOOP)), y = SWEEPER.y, h = tall(y);
  person(x, y, SWEEPER.L, { h, view: 'side', dir, arm: 'work', a: 0.25 });
  R(x - dir * 6, y - Math.round(h * 0.42), 2, 1, '#f0f0d0');                    // the uniform's reflective band
  const hx = x + dir * 4, hy = y - Math.round(h * 0.45), bx = x + dir * 9 + sweep;   // the broom: a bamboo handle and its twigs
  const n = Math.max(Math.abs(bx - hx), y - 1 - hy);
  for (let k = 0; k <= n; k++) R(Math.round(lerp(hx, bx, k / n)), Math.round(lerp(hy, y - 1, k / n)), 1, 1, '#c8a868');
  R(bx - 2, y - 1, 5, 1, '#a88a50'); R(bx - 3, y, 7, 1, '#8a6a3a');
}
// [feet row, crossings a loop, direction, phase, look, kind, rank]; lane A runs the whole width behind the corner,
// lane B only from the corner out to the right (people leaving with their bánh mì, or coming for one)
const WALKERS = [
  [199, 5, 1, 0.1, look(2, 0, '#5a6ab0', '#2a2a34'), 'walk', 0.03],
  [200, 12, -1, 0.3, look(1, 1, '#e8e8e8', '#2a2a40', { long: true }), 'jog', 0.22],
  [199, 4, -1, 0.55, look(0, 2, '#b8463a', '#3a3440'), 'dog', 0.38],
  [199, 3, 1, 0.8, look(3, 0, '#e0d0a0', '#4a4a5a'), 'walk', 0.47],
  [200, 6, 1, 0.45, look(2, 1, '#ffb0c8', '#2a2a3a', { long: true, skirt: true }), 'tea', 0.66],
  [199, 5, -1, 0.92, look(1, 3, '#3a3a48', '#5a5a6a'), 'walk', 0.84],
  [205, 4, 1, 0.2, look(0, 1, '#f0e6d0', '#3a2a2a', { long: true }), 'umbrella', 0.15],
  [205, 3, -1, 0.7, look(1, 0, '#9ab0d0', '#2a2a34'), 'walk', 0.56],
].map(([y, k, dir, ph, L, kind, rank]) => ({ y, k, dir, ph, L: y < 202 ? night(L, 0.1) : L, kind, rank, half: y > 202 }));
function walkerAt(w, t) {
  const u = frac(w.k * t / LOOP + w.ph);
  if (!w.half) return { x: Math.round(w.dir > 0 ? -12 + u * (W + 24) : W + 12 - u * (W + 24)), a: 1 };
  const x = Math.round(w.dir > 0 ? LANE_B + u * (W + 12 - LANE_B) : W + 12 - u * (W + 12 - LANE_B));
  return { x, a: clamp((x - LANE_B) / 14, 0, 1) };  // out of the corner's shade, or into it
}
function drawWalker(w, t, shower) {
  const { x, a } = walkerAt(w, t), h = tall(w.y), jog = w.kind === 'jog';
  if (a <= 0) return;
  ctx.globalAlpha = a;
  person(x, w.y, w.L, { h, view: 'side', dir: w.dir, pose: 'walk', stride: Math.floor(x / (jog ? 2 : 3)) % 2, arm: w.kind === 'tea' ? 'cup' : 'down' });
  if (w.kind === 'dog') {                          // a small dog on a lead, trotting ahead
    const dx = x + w.dir * 9, f = Math.floor(x / 2) % 2;
    R(dx - 2, w.y - 3, 5, 2, '#c8a070'); R(dx + w.dir * 3, w.y - 4, 2, 2, '#c8a070'); R(dx + w.dir * 4, w.y - 4, 1, 1, EYE_C);
    R(dx - 2 + f, w.y - 1, 1, 2, '#8a6a4a'); R(dx + 1 - f, w.y - 1, 1, 2, '#8a6a4a'); R(dx - w.dir * 3, w.y - 4, 1, 1, '#c8a070');
    for (let k = 1; k < 7; k++) R(x + w.dir * (2 + k), Math.round(w.y - 13 + k * 1.3), 1, 1, '#5a5a6a');
  }
  if (w.kind === 'umbrella' && shower > 0.25) {    // the shower: out comes her umbrella
    const top = w.y - h - 3;
    R(x - 6, top, 12, 2, '#3a6ab0'); R(x - 4, top - 1, 8, 1, '#4a7ac0'); R(x - 7, top + 2, 1, 1, '#2a4a80'); R(x + 6, top + 2, 1, 1, '#2a4a80');
    R(x, top + 2, 1, 6, '#2a2a30');
  }
  ctx.globalAlpha = 1;
}

/* ================= Timed things in the sky and on the towers ================= */
// planes coming down to Tan Son Nhat, off to the north-west: in from the right, sinking towards the towers
const PLANES = [10, 90, 170], PLANE_DUR = 24;
function drawPlanes(t) {
  for (const t0 of PLANES) {
    if (t < t0 || t > t0 + PLANE_DUR) continue;
    const u = (t - t0) / PLANE_DUR, x = Math.round(lerp(W + 6, 236, u)), y = Math.round(lerp(22, 90, u)), a = 1 - smooth(0.72, 1, u);
    ctx.globalAlpha = a;
    R(x - 2, y, 5, 1, '#3a3a52'); R(x + 2, y - 1, 1, 1, '#3a3a52'); R(x - 2, y, 1, 1, '#fff8e8'); R(x + 2, y, 1, 1, '#f0f0ff');
    if (frac(t / 1.2) < 0.18) R(x, y + 1, 1, 1, '#ff3a2a');
    ctx.globalAlpha = 1;
    if (frac(t / 1.5 + 0.3) < 0.06) { R(x, y - 1, 1, 1, '#ffffff'); halo(HL.strobe, x, y, a); }
  }
}
// Landmark 81's show: bands of colour climb the tubes for half a minute, then its warm white comes back
const LM_SHOW = [156, 186];
function drawLMShow(t) {
  const a = env(t, LM_SHOW[0], LM_SHOW[1], 3);
  if (a <= 0) return;
  const { cx, foot, crown, tip } = LM, h = foot - tip, s = step(t, 960) % 64;
  ctx.globalAlpha = a * 0.85;
  for (const [dx, w, f] of LM_TUBES) {
    const x0 = cx + dx, ty = f ? foot - Math.round(f * h) : crown;
    for (let i = x0; i < x0 + w - 1; i += 2) for (let y = ty + 2; y < foot; y += 4) {
      ctx.fillStyle = SHOW_COLS[(((foot - y) >> 2) + 64 - s + (i - x0)) % SHOW_COLS.length];
      ctx.fillRect(i, y, 1, Math.min(4, foot - y));
    }
  }
  ctx.globalAlpha = 1;
  halo(HL.lmShow, cx, crown + 12, a * 0.8);
}
// the Ba Son pylon's floodlights run through four colours, seven seconds each
const PYLON_SHOW = [108, 136], PYLON_COLS = ['#5a8aff', '#a86aff', '#ff6ab8', '#ffb05a'];
function drawPylonShow(t) {
  const a = env(t, PYLON_SHOW[0], PYLON_SHOW[1], 3);
  if (a <= 0) return;
  const k = Math.min(3, Math.floor((t - PYLON_SHOW[0]) / 7));
  ctx.globalAlpha = a * 0.7; ctx.fillStyle = PYLON_COLS[k];
  for (let y = 109; y <= BS.foot; y++) { ctx.fillRect(Math.round(legX(y)), y, 2, 1); ctx.fillRect(Math.round(legX(y, true)), y, 1, 1); }
  ctx.globalAlpha = 1;
  halo(HL.pylonShow[k], 398, 132, a * 0.9);
}

/* ================= Small life: moths, the brazier, the shower, the drips, the gecko and the bat ================= */
const MOTH_AT = [...LAMPS.map(x => [x + 1, LAMP_Y]), ...BULBS_C.map(b => [b.x, b.y + 1])];
function drawMoths(t) {
  ctx.fillStyle = '#e8e0d0';
  MOTH_AT.forEach(([mx, my], i) => { for (let k = 0; k < 3; k++) {
    const ph = hash(i, k, 61), n = 70 + k * 13 + i * 7, r = 4 + 3 * Math.sin(TAU * ((11 + k) * t / LOOP + ph * 5));
    ctx.fillRect(Math.round(mx + Math.cos(TAU * (n * t / LOOP + ph)) * r), Math.round(my + Math.sin(TAU * ((n + 3) * t / LOOP + ph * 2)) * r * 0.6), 1, 1);
  } });
}
function drawBrazier(t, fanning) {
  const s = step(t, 1200);
  for (let x = BRAZIER.x0 + 1; x < BRAZIER.x1; x++) R(x, BRAZIER.top + 1, 1, 1, hash(x, s) < 0.5 ? '#ff8a2a' : '#ffc04a');
  for (let x = BRAZIER.x0 + 1; x < BRAZIER.x1; x += 2) R(x, BRAZIER.top - 1, 1, 1, '#c8703a');   // skewers of pork on the grate
  halo(HL.coal, (BRAZIER.x0 + BRAZIER.x1) >> 1, BRAZIER.top, fanning ? 1 : 0.7);
  for (let i = 0; i < 10; i++) {
    const { cyc, u } = cycle(t, 96, i / 10);
    const x = Math.round(BRAZIER.x0 + 4 + Math.sin(u * 5 + i) * 1.5 + u * 8 + hash(i, cyc, 2) * 2), y = Math.round(BRAZIER.top - 2 - u * 30);
    ctx.globalAlpha = (1 - u) * (fanning ? 0.5 : 0.3); R(x, y, u > 0.4 ? 2 : 1, 1, '#c8c0c8');
  }
  ctx.globalAlpha = 1;
}
// a warm shower passes late in the loop; the tamarind drips for a while after it
const SHOWER = [188, 216];
const showerAt = t => env(t, SHOWER[0], SHOWER[1], 5);
const dripsAt = t => smooth(SHOWER[0] + 6, SHOWER[0] + 14, t) * (1 - smooth(SHOWER[1] + 4, SHOWER[1] + 20, t));
function drawDrips(t, amount) {
  if (amount <= 0.02) return;
  ctx.fillStyle = '#b8c0e0';
  for (let i = 0; i < 12; i++) {
    const { cyc, u } = cycle(t, 150, i / 12);
    if (hash(i, cyc, 8) > amount) continue;
    const x = 6 + Math.floor(hash(i, cyc, 9) * 116), y0 = 100 + hash(i, cyc, 4) * 10;
    ctx.globalAlpha = 0.6; ctx.fillRect(x, Math.round(lerp(y0, 204, u * u)), 1, 2);
  }
  ctx.globalAlpha = 1;
}
function drawRings(t, amount) {
  if (amount <= 0.05) return;
  ctx.fillStyle = '#9aa0c8';
  PUDDLES.forEach(([cx, cy, rx, ry], j) => { for (let k = 0; k < 3; k++) {
    const { cyc, u } = cycle(t, 90, (j * 3 + k) / 18);
    if (hash(j, k, cyc) > amount) continue;
    const x = Math.round(cx + (hash(j, cyc, k) - 0.5) * rx * 1.2), y = Math.round(cy + (hash(cyc, j, k) - 0.5) * ry), r = 1 + Math.round(u * 3);
    ctx.globalAlpha = (1 - u) * 0.6;
    ctx.fillRect(x - r, y, 1, 1); ctx.fillRect(x + r, y, 1, 1); ctx.fillRect(x - (r >> 1), y - 1, r, 1); ctx.fillRect(x - (r >> 1), y + 1, r, 1);
  } });
  ctx.globalAlpha = 1;
}
// hidden things: a cat asleep on the parked scooter's seat, a gecko on a lamp post, a bat hunting the lamp's moths
const CAT = { x: PARKED.x + 21, y: FEET_S - 19 };
function drawCat(c) {
  const px = (x, y, col) => { c.fillStyle = col; c.fillRect(CAT.x + x, CAT.y + y, 1, 1); };
  px(0, 0, '#c87a3a'); px(2, 0, '#c87a3a');
  for (let x = 0; x < 7; x++) px(x, 1, x < 3 ? '#d88a4a' : x === 4 ? '#8a4a22' : '#d88a4a');
  for (let x = 0; x < 8; x++) px(x, 2, x === 2 || x === 5 ? '#8a4a22' : '#c87a3a');
  px(1, 1, '#3a2a1a'); px(7, 1, '#8a4a22');
}
const GECKO = { x: LAMPS[0] - 1, y: 160 };
function drawGecko(t) {
  const { x, y } = GECKO, turn = frac(t / 30) < 0.15 ? 1 : 0;
  R(x + 1, y + 1, 2, 5, '#b8a880'); R(x + 1 + turn, y, 1, 1, '#c8b890');
  for (const [fx, fy] of [[0, 2], [3, 2], [0, 5], [3, 5]]) R(x + fx, y + fy, 1, 1, '#a89870');
  R(x + 2, y + 6, 1, 2, '#a89870'); R(x + 1, y + 8, 1, 1, '#a89870');
}
const BAT = { x: LAMPS[0] + 1, y: 120, at: [[40, 64], [150, 174]] };
function drawBat(t) {
  const win = BAT.at.find(([a, b]) => t >= a && t < b);
  if (!win) return;
  const a = env(t, win[0], win[1], 2), x = Math.round(BAT.x + 7 * Math.sin(TAU * 40 * t / LOOP)), y = Math.round(BAT.y + 3 * Math.sin(TAU * 70 * t / LOOP)), up = step(t, 2400) % 2;
  ctx.globalAlpha = a;
  R(x - 1, y, 3, 1, '#1a1420'); R(x - 3, y - up, 2, 1, '#241c2c'); R(x + 2, y - up, 2, 1, '#241c2c'); R(x, y - 1, 1, 1, '#1a1420');
  ctx.globalAlpha = 1;
}

/* ================= Weather: the city's own is a warm shower that comes and goes; a pick falls on the river and the bank ================= */
const WEATHER = weatherPlans(['drizzle']);
// the tamarind keeps the middle and near depths off the corner: from its crown's lower edge to the paving, out to the pole
const UNDER_TREE = [0, 104, LIMB_END, NEAR];
const WX_LIGHTS = [...BULBS_C.map(b => ({ x: b.x + 1, y: b.y + 3, rx: 28, ry: 26 })), ...LAMPS.map(x => ({ x: x + 1, y: LAMP_Y + 4, rx: 22, ry: 26 }))];
const WX = { land: [[HZ - 8, HZ + 4], [HZ + 4, EDGE], [PROM + 6, H + 4]], lights: WX_LIGHTS, shelter: [[], [UNDER_TREE], [UNDER_TREE]], density: 0.9 };
const WX_BB = { ...WX, lights: [...WX_LIGHTS, { x: BB.x + 24, y: BB.y + 12, rx: 30, ry: 18, col: '#ffb8c4' }] };
// the city's own shower is a light rain at half the engine's count, fading in and out; a picked drizzle is drizzle all loop
function weatherAt(depth, t, wx, cfg, own, reducedMotion, picked) {
  drawWeather(ctx, wx.shared, depth, t, cfg, reducedMotion);
  if (own > 0.01) drawWeather(ctx, picked ? 'drizzle' : 'rain', depth, t, { ...cfg, density: picked ? 1 : own * 0.5 }, reducedMotion);
}

function renderVignette() {
  const p = painter(W, H);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const d = Math.hypot((x - W / 2) / (W * 0.62), (y - H * 0.45) / (H * 0.7));
    if (d > 0.84 && (d - 0.84) * 3 > bayer(x, y)) p.put(x, y, '#05040e', 90);
  }
  return p.done();
}

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, WISP_C, SKYLINE_C, BIT_C, FRONT_C, BRIDGE_C, NORTH_C, WATER_C, MOORED_C, CRUISE_C, WATERBUS_C, BANK_C, DRY_C,
  HEDGE_C, LAMPS_C, CORNER_C, CANOPY_C, BBF_C, BB_AD, PITCH, VIG_C, RIDE_L, RIDE_R;
function prerender() {
  HL = {
    moon: makeHalo(28, '#ffe8c8', 0.12, 1.8), glow: makeHalo(150, '#ff8a50', 0.1, 1.4),
    crown: makeHalo(18, '#e8f0ff', 0.16, 1.5), red: makeHalo(3, '#ff3a2a', 0.6, 1.2), gold: makeHalo(22, '#ffc060', 0.12, 1.5),
    lm: makeHalo(16, '#fff0d0', 0.12, 1.5), pylon: makeHalo(20, '#e8e4ff', 0.1, 1.5), lmShow: makeHalo(24, '#ff7ad0', 0.12, 1.5),
    pylonShow: PYLON_COLS.map(c => makeHalo(22, c, 0.14, 1.5)),
    lamp: makeHalo(26, '#ffc070', 0.26, 1.6), lampCore: makeHalo(7, '#ffe8b0', 0.45, 1.4),
    bulb: makeHalo(34, '#ffc070', 0.3, 1.5), bulbCore: makeHalo(8, '#fff0c0', 0.5, 1.3), glass: makeHalo(24, '#fff0c8', 0.16, 1.5),
    string: makeHalo(7, '#ffc070', 0.3, 1.4), coal: makeHalo(10, '#ff7a2a', 0.35, 1.4), sign: makeHalo(34, '#ff9aa8', 0.08, 1.5),
    boat: makeHalo(6, '#ffe0a8', 0.35, 1.3), cool: makeHalo(8, '#cfe6ff', 0.3, 1.3), head: makeHalo(8, '#fff4d0', 0.4, 1.4),
    toy: makeHalo(5, '#c8f0ff', 0.45, 1.3), strobe: makeHalo(5, '#ffffff', 0.5, 1.3), phone: makeHalo(4, '#c8e0ff', 0.4, 1.3),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); WISP_C = WISPS.map(renderWisp);
  layoutBack(); bitEdges(); layoutBitFloors();
  const sk = painter(W, H);
  for (const T of BACK) drawTower(sk, T);
  for (const T of MID) drawTower(sk, T);
  renderTimesSquare(sk); renderVietcombank(sk); renderLandmark81(sk);
  SKYLINE_C = sk.done();
  BIT_C = renderBitexco(); FRONT_C = renderFront(); BRIDGE_C = renderBridge(); NORTH_C = renderNorthBank();
  WATER_C = renderWater(); MOORED_C = MOORED.map(renderMoored); CRUISE_C = renderCruise(); WATERBUS_C = renderWaterbus();
  BANK_C = renderBank(); DRY_C = renderDry(); HEDGE_C = renderHedge();
  const lp = makeCanvas(W, H), lx = lp[1];
  for (const x of LAMPS) lx.drawImage(renderLamp(x), 0, 0);
  LAMPS_C = lp[0];
  RIDE_L = scooterSprite('#c8c8d0', '#7a7a88', false); RIDE_R = scooterSprite('#c8c8d0', '#7a7a88', true);
  CORNER_C = renderCorner();
  const cc = CORNER_C.getContext('2d');
  cc.drawImage(scooterSprite('#4a6a9a', '#2a4466', false), PARKED.x, FEET_S - 21);
  drawCat(cc);
  sign(cc, 'BÁNH', CART.x0 + 10, CART.feet - 50, '#fff2d8'); sign(cc, 'MÌ', CART.x0 + 30, CART.feet - 50, '#fff2d8');
  CANOPY_C = [-1, 0, 1].map(renderCanopy);
  BBF_C = renderBillboardFrame(); BB_AD = renderBillboardAd();
  PITCH = makePitch(BB.w, BB.h, { accent: '#ff5a82' });
  VIG_C = renderVignette();
}

/* ================= Frame compositor (back to front) ================= */
const blit = (c, x, y, w, h) => ctx.drawImage(c, x, y, w, h, x, y, w, h);   // a full-frame layer, only where it has pixels
function drawScroll(cv, y, k, t) {
  const off = Math.floor(frac(k * t / LOOP) * W);
  ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y);
}
function drawBillboard(t, pitch) {
  halo(HL.sign, BB.x + (BB.w >> 1), BB.y + 12, 0.9);
  blit(BBF_C, BB.x - 2, TREE.limb - 4, BB.w + 4, BB.y + BB.h + 6 - TREE.limb);
  if (!PITCH.covers(t, pitch)) ctx.drawImage(BB_AD, BB.x, BB.y);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
}
// motorbikes on Ton Duc Thang: a headlight and a tail light each, both ways
const BIKES = [...Array(14)].map((_, i) => ({ k: 3 + (i % 4), ph: hash(i, 21), dir: i % 2 ? 1 : -1 }));
function drawBikes(t) {
  for (const b of BIKES) {
    const u = frac(b.k * t / LOOP + b.ph), x = Math.round(b.dir > 0 ? u * 380 - 4 : 376 - u * 380), y = ROAD + (b.dir > 0 ? 1 : 0);
    if (x < 0 || x > 371) continue;
    R(x, y, 1, 1, '#fff8e8'); R(x - 2 * b.dir, y, 1, 1, '#ff3a2a');
  }
}
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false, crowd = CROWD } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over'; ctx.imageSmoothingEnabled = false;
  const wx = WEATHER(weather), cfg = billboard ? WX_BB : WX, own = wx.drizzle ? (weather === 'drizzle' ? 1 : showerAt(t)) : 0;
  // the sky: stars, the moon, the last of the rain clouds drifting off, the city's glow, a plane coming down
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#eef0ff';
  for (const s of STARS) {
    ctx.globalAlpha = s.b * (0.3 + 0.7 * (0.5 + 0.5 * wave(t, s.k, s.ph)));
    ctx.fillRect(s.x, s.y, 1, 1);
    if (s.big) { ctx.globalAlpha *= 0.45; ctx.fillRect(s.x - 1, s.y, 3, 1); ctx.fillRect(s.x, s.y - 1, 1, 3); }
  }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y + 1, 0.9);
  ctx.drawImage(MOON_C, MOON.x - MOON.r, MOON.y - MOON.r);
  for (const L of WISP_C) drawScroll(L.c, L.y, L.k, t);
  halo(HL.glow, 240, HZ, 0.9);
  drawPlanes(t);
  // District 1's towers, their windows, Times Square's gold, the Vietcombank crown, Landmark 81 beyond the bridge
  ctx.drawImage(SKYLINE_C, 0, 0);
  const ws = step(t, 40);
  for (const w of WINS) R(w.x, w.y, w.n, 1, hash(w.x, w.y, ws) < 0.6 ? w.col : w.off);
  halo(HL.gold, (TSQ.x0 + TSQ.x1) >> 1, TSQ.top + 20, 0.8);
  for (let i = TSQ.x0 + 1, k = 0; i < TSQ.x1; i += 3, k++) {           // Times Square's gold lines: light runs up them
    const y = Math.round(HZ - (HZ - TSQ.top) * frac(t / 6 + k * 0.09));
    R(i, y, 1, 5, '#fff2c0');
  }
  for (const [x0, x1, top, kind] of MID) {        // LED facades: the gold-ruled towers chase light up, the pink bands run down
    if (kind === 'g') for (let i = x0 + 1, k = 0; i < x1 - 1; i += 3, k++) R(i, Math.round(HZ - (HZ - top) * frac(t / 5 + k * 0.13 + x0 * 0.01)), 1, 4, '#ffe6a0');
    else if (kind === 'p') { const off = Math.floor(frac(t / 4) * 6); for (let y = top + 4 + off; y < HZ; y += 6) R(x0 + 1, y, x1 - x0 - 2, 1, '#ffa8d8'); }
  }
  halo(HL.lm, LM.cx, LM.crown, 0.8);
  drawLMShow(t);
  const blink = frac(t / 1.5) < 0.5;
  if (blink) { R((TSQ.x0 + TSQ.x1) >> 1, TSQ.top - 6, 1, 1, '#ff4a3a'); R(LM.cx, LM.tip - 1, 1, 1, '#ff4a3a'); halo(HL.red, LM.cx, LM.tip - 1, 0.8); }
  else { R(VCB_MAST, VCB.spire - 1, 1, 1, '#ff4a3a'); halo(HL.red, VCB_MAST, VCB.spire - 1, 0.8); }
  // Bitexco
  ctx.drawImage(BIT_C, 0, 0);
  drawBitexcoLights(t);
  // the riverfront, the bridge and its pylon, the boats at Bach Dang
  ctx.drawImage(FRONT_C, 0, 0);
  drawBikes(t);
  halo(HL.pylon, 398, 128, 0.7);
  blit(BRIDGE_C, 330, 104, 150, 62);
  drawPylonShow(t);
  drawMoored(t);
  // the river: its colour, the mirrored skyline, the streaks of the brightest lights, the north bank, the boats, hyacinths
  ctx.drawImage(WATER_C, 0, HZ);
  reflect(ctx, t, { top: HZ, rows: EDGE - HZ, squash: 1.6, alpha: 0.42, amp: 0.7, grow: 0.06, k1: 40, k2: 96 });
  streak(ctx, t, 156, 8, '#e8f0ff', 0.32, 1, HZ + 1, EDGE);
  streak(ctx, t, (TSQ.x0 + TSQ.x1) >> 1, 12, '#ffc060', 0.36, 2, HZ + 1, EDGE);
  streak(ctx, t, 258, 10, '#ffe0a0', 0.3, 3, HZ + 1, EDGE);
  streak(ctx, t, 292, 6, '#e0e8ff', 0.26, 4, HZ + 1, EDGE);
  for (const B of MOORED) streak(ctx, t, B.x + (B.w >> 1), 14, '#ffd890', 0.3, B.x, HZ + 1, EDGE);
  blit(NORTH_C, 370, 150, 110, 14);
  streak(ctx, t, LM.cx, 6, '#fff0d0', 0.26, 5, 164, EDGE);
  streak(ctx, t, 399, 4, '#e8e4ff', 0.22, 7, 164, EDGE);
  drawHyacinths(t);
  drawWaterbus(t);
  drawCruise(t);
  // our bank, then everyone on it in the order of their feet
  ctx.drawImage(BANK_C, 0, 0);
  weatherAt(0, t, wx, cfg, own, reducedMotion, weather === 'drizzle');
  const DL = [];
  DL.push([PROM + 1, () => blit(LAMPS_C, 0, LAMP_Y - 3, W, PROM - LAMP_Y + 5)]);
  if (COUPLE.rank < crowd) DL.push([COUPLE.y, () => {
    const lean = frac(t / 40) > 0.7 ? -1 : 0;
    person(COUPLE.x, COUPLE.y, COUPLE.him, { h: tall(COUPLE.y), view: 'back' });
    person(COUPLE.x + 7, COUPLE.y, COUPLE.her, { h: tall(COUPLE.y) - 2, view: 'back', lean });
  }]);
  if (FILMER.rank < crowd) DL.push([FILMER.y, () => {
    const up = inEvent(t, [LM_SHOW]) || frac(t / 24) > 0.4;
    person(FILMER.x, FILMER.y, FILMER.L, { h: tall(FILMER.y), view: 'back', arm: up ? 'phone' : 'down' });
    if (up) halo(HL.phone, FILMER.x + 3, FILMER.y - tall(FILMER.y) + 8, 0.7);
  }]);
  if (crowd > 0) { DL.push([TOYMAN.y, () => drawToyman(t)]); DL.push([TOYMAN.y + 0.1, () => drawToys(t)]); }
  for (const w of WALKERS) if (w.rank < crowd) DL.push([w.y, () => drawWalker(w, t, own)]);
  DL.push([203.5, () => { blit(CORNER_C, 0, 90, PARKED.x + 38, 118); drawBrazier(t, cycle(t, 12).u > 0.7); }]);
  if (crowd > 0) {
    DL.push([VENDOR.y + 0.1, () => drawVendor(t)]);
    drawRider(t, DL);
    DL.push([SWEEPER.y, () => drawSweeper(t)]);
  }
  for (const s of SITTERS) if (s.rank < crowd) DL.push([FEET_S, () => drawSitter(s, t)]);
  DL.sort((a, b) => a[0] - b[0]);
  for (const [, draw] of DL) draw();
  // light: the bulb over the cart, its glass case, the string of bulbs, the lamps; moths, the gecko, the bat
  const fl = 0.85 + 0.15 * hash(3, step(t, 960));
  BULBS_C.forEach((b, i) => { const f = i ? 0.9 : fl; R(b.x, b.y, 2, 2, '#fff8e0'); halo(HL.bulb, b.x + 1, b.y + 3, f); halo(HL.bulbCore, b.x + 1, b.y + 1, f); });
  halo(HL.glass, (CART.x0 + CART.x1) >> 1, CART.feet - 32, 0.7);
  for (const x of LAMPS) { halo(HL.lamp, x + 1, LAMP_Y + 2, 0.85); halo(HL.lampCore, x - 3, LAMP_Y, 0.6); halo(HL.lampCore, x + 5, LAMP_Y, 0.6); }
  drawMoths(t);
  drawGecko(t);
  drawBat(t);
  // the wet paving mirrors the corner, the railing and the river; the puddles mirror them most
  reflect(ctx, t, { top: NEAR, rows: LAWN - NEAR, squash: 2.4, alpha: 0.5, amp: 0.4, grow: 0.03, k1: 30, k2: 70 });
  blit(DRY_C, 0, NEAR, W, LAWN - NEAR);
  for (const x of LAMPS) streak(ctx, t, x + 1, 4, '#ffc880', 0.28, x * 0.1, NEAR, LAWN);
  streak(ctx, t, BULB.x + 1, 16, '#ffc070', 0.26, 8, NEAR, LAWN);
  if (wx.drizzle) drawRings(t, own);
  weatherAt(1, t, wx, cfg, own, reducedMotion, weather === 'drizzle');
  blit(HEDGE_C, 0, LAWN - 8, W, H - LAWN + 8);
  // the sign under the tamarind, the crown over it all, the drips after the shower
  if (billboard) drawBillboard(t, pitch);
  const br = wave(t, 30) + 0.5 * wave(t, 77, 0.3);
  blit(CANOPY_C[br < -0.5 ? 0 : br > 0.5 ? 2 : 1], 0, 0, 134, 116);
  if (wx.drizzle) drawDrips(t, weather === 'drizzle' ? 0.6 : dripsAt(t));
  weatherAt(2, t, wx, cfg, own, reducedMotion, weather === 'drizzle');
  ctx.drawImage(VIG_C, 0, 0);
}

const CROWD = 0.5;
export default {
  id: 'ho-chi-minh-city',
  name: 'Ho Chi Minh City',
  tagline: 'Saigon River after the rain',
  timeZone: 'Asia/Ho_Chi_Minh',
  accent: '#c9184a',
  focusX: 153,
  landmark: [134, 172],                            // Bitexco from the helipad's rim to its north face
  landmarkTop: BX.tip,
  billboard: BB,
  crowd: CROWD,
  spots: [                                         // what answers a tap (site/js/ui/spots.js)
    { kind: 'landmark', x: 138, y: BX.tip - 2, w: 32, h: 118 },                       // Bitexco
    { kind: 'landmark', x: 386, y: 106, w: 18, h: 52 },                                // the Ba Son Bridge's pylon
    { kind: 'window', x: BULBS_C[1].x - 3, y: BULBS_C[1].y - 3, w: 8, h: 8 },          // the bare bulb over the first table
    { kind: 'water', x: 270, y: 164, w: 96, h: 12 },                                   // the river, clear of the gecko
  ],
  ambience: { rain: 0.15, water: 0.3, waves: 0.15, traffic: 0.4, crowd: 0.25, crickets: 0.25, wind: 0.1 },
  events: [
    { t: CRUISE.t0, type: 'boat', dur: CRUISE.t1 - CRUISE.t0, pan: [1, -1], gain: 0.3 },   // the dinner cruise heads downstream
    { t: CRUISE.t0 + 4, type: 'horn', x: 470, gain: 0.3 },                                  // and sounds its horn coming in
    { t: WATERBUS.t0, type: 'boat', dur: WATERBUS.t1 - WATERBUS.t0, pan: [-1, 1], gain: 0.22 },   // the waterbus heads upstream
    { t: BIT_SHOW[0], type: 'chime', x: 153, gain: 0.2 },                                   // Bitexco's crown starts its show
    { t: LM_SHOW[0], type: 'chime', x: 433, gain: 0.22 },                                   // Landmark 81's show
  ],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
